import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Badge,
  Card,
  Row,
  Col,
  Tabs,
  Table,
  Button,
  Tag,
  Tooltip,
  Icon,
  Modal,
  DatePicker,
  Input,
  Select,
  Progress,
  message,
  Radio,
  Form,
  Timeline
} from 'antd';
import moment from 'moment';
import { Chart, Geom, Axis, Tooltip as ChartTooltip, Coord, Label, Legend } from 'bizcharts';
import DataSet from '@antv/data-set';
import { constructColumn, constructColumnFilterSearch, constructColumnFilterRadio, handleOnAggregationTableChange } from 'utils/table/table';
import { StyledCard, KPICard, SectionTitle } from './QualityDashboard.styles';
import { getAirbnbQualityDashboardAccounts } from 'utils/apis/integration';
import {
  useGetPaginatedAirbnbQualityDashboard,
  getReviewTrends,
  getComponentEvolution,
  getReviewQualitySummary,
  getReviewQualityTags,
  getReviewQualityViolations,
  getReviewQualityAccommodationStatus,
  getQualityDashboardDetailedReviewsReport,
  getQualityDashboardListingIssues,
  getCountriesConstant,
  getStatesConstant,
  CHECKOUT_TASKS,
  HOST_NOTIFICATIONS_SEED,
  GROUND_RULE_NOTIFICATIONS,
  STATUS_COLOR,
  SEVERITY_COLOR
} from './QualityDashboardDemo7.dummyData';
import QualityDashboardQualityStatsSection from './QualityDashboardQualityStatsSection';

const { RangePicker } = DatePicker;
const { Option } = Select;

const QUALITY_STATUS_META = {
  GOOD: {
    label: 'Good',
    color: 'green',
    actionRequired: 'Maintain current standards.'
  },
  EDUCATE: {
    label: 'Educate',
    color: 'gold',
    actionRequired: 'Identify and fix the issue.'
  },
  WARN: {
    label: 'Warn',
    color: 'orange',
    actionRequired: 'Immediate attention required; listing at risk.'
  },
  WARNING: {
    label: 'Warning',
    color: 'orange',
    actionRequired: 'Immediate attention required; listing at risk.'
  },
  PROBATION: {
    label: 'Probation',
    color: 'orangered',
    actionRequired: 'CRITICAL: Resolve issues immediately.'
  },
  ADDITIONAL_WARN: {
    label: 'Additional warn',
    color: 'orange',
    actionRequired: 'URGENT: Immediate, drastic intervention required.'
  },
  PENDING_REMOVAL: {
    label: 'Pending removal',
    color: 'red',
    actionRequired: 'CRITICAL: Contact Airbnb to prevent permanent removal.'
  },
  SUSPENDED: {
    label: 'Suspended',
    color: 'red',
    actionRequired: 'INACTIVE LISTING: Contact Airbnb to reinstate.'
  },
  SUSPEND: {
    label: 'Suspend',
    color: 'red',
    actionRequired: 'INACTIVE LISTING: Contact Airbnb to reinstate.'
  },
  REMOVED: {
    label: 'Removed',
    color: 'red',
    actionRequired: 'Listing is offline permanently.'
  }
};

const KPI_STATUS_FILTER_VALUE = {
  GOOD: 'good',
  WARNING: 'warning',
  AT_RISK: 'at_risk',
  SUSPENDED: 'suspended'
};
const QUALITY_STATUS_FAMILY_KEYS = Object.values(KPI_STATUS_FILTER_VALUE);

const KPI_STATUS_META = {
  [KPI_STATUS_FILTER_VALUE.GOOD]: {
    label: 'Good',
    tooltip: 'Listings in Good and Educate status.'
  },
  [KPI_STATUS_FILTER_VALUE.WARNING]: {
    label: 'Warn family',
    tooltip: 'Listings in Warning status.'
  },
  [KPI_STATUS_FILTER_VALUE.AT_RISK]: {
    label: 'At risk group',
    tooltip: 'Listings in Probation, Additional warn, and Pending removal status.'
  },
  [KPI_STATUS_FILTER_VALUE.SUSPENDED]: {
    label: 'Suspended group',
    tooltip: 'Listings in Suspended and Removed status.'
  }
};

const TOOLTIP_COPY = {
  trends: {
    averageReviewScore:
      'Average of guest overall ratings for each period (sum of overall ratings / total reviews), based on current filters and date range.',
    fiveStarReviewPct: 'Percentage of reviews with overall rating = 5 for each period, based on current filters and date range.',
    wowYoyDelta: 'WoW/MoM compares against the previous completed period. YoY compares against the same period last year.',
    yoyDelta: 'Difference from the same period last year.'
  },
  componentEvolution: {
    chart: 'Average category rating by period for current filters and date range. Missing values can be hidden for incomplete periods.'
  },
  reviews: {
    categoryScoreDistribution: 'Positive means ratings 4-5; negative means ratings below 4.',
    violations: 'Total violations matched by current filters and date range.',
    accommodationStatus: 'Status counts across matched listings for current filters and date range.'
  },
  emptyState: {
    noData: 'No matched data for current filters.'
  }
};

function mapQualityStatusFilterToQueryValue(filterValue) {
  if (!filterValue || filterValue === 'ALL') return undefined;
  const v = String(filterValue);
  const normalizedFamily = v.trim().toLowerCase();
  if (QUALITY_STATUS_FAMILY_KEYS.includes(normalizedFamily)) return normalizedFamily;
  return v;
}

function mapQualityStatusFilterToTagLabel(filterValue) {
  if (!filterValue || filterValue === 'ALL') return null;
  const v = String(filterValue).trim();
  const normalizedFamily = v.toLowerCase();
  if (normalizedFamily === 'good') return 'Good';
  if (normalizedFamily === 'warning') return 'Warn';
  if (normalizedFamily === 'at_risk') return 'At risk group';
  if (normalizedFamily === 'suspended') return 'Suspended group';
  return (KPI_STATUS_META[filterValue] && KPI_STATUS_META[filterValue].label) || v;
}

function mapQualityStatusFilterToModalValue(filterValue) {
  if (!filterValue || filterValue === 'ALL') return 'ALL';
  const v = String(filterValue).trim();
  const normalizedFamily = v.toLowerCase();
  if (QUALITY_STATUS_FAMILY_KEYS.includes(normalizedFamily)) return normalizedFamily;
  return v;
}

/** Flat filter params for trends, review quality, and report — server applies listing/geo/KPI filters. */
function buildSharedQualityDashboardFilters({
  listingFilter,
  cityFilter,
  countryFilter,
  stateFilter,
  qualityStatusFilter,
  kpiStatusDrilldown,
  selectedAirbnbUserId,
  startDate,
  endDate
}) {
  const q = {
    airbnbUserId: selectedAirbnbUserId || undefined,
    listing: listingFilter.trim() || undefined,
    city: cityFilter.trim() || undefined,
    country: countryFilter.trim() || undefined,
    state: stateFilter.trim() || undefined,
    quality_status: mapQualityStatusFilterToQueryValue(qualityStatusFilter),
    kpiStatus: kpiStatusDrilldown && kpiStatusDrilldown !== 'ALL' ? kpiStatusDrilldown : undefined,
    startDate,
    endDate
  };
  Object.keys(q).forEach(k => q[k] === undefined && delete q[k]);
  return q;
}

function normalizeQualityStatusCode(value) {
  const raw = value == null ? '' : String(value);
  const normalized = raw
    .trim()
    .toUpperCase()
    .replace(/\s+/g, '_');
  const keys = Object.keys(QUALITY_STATUS_META);
  const matchKey = keys.find(code => {
    if (code === normalized) return true;
    const meta = QUALITY_STATUS_META[code];
    if (!meta || !meta.label) return false;
    return (
      String(meta.label)
        .toUpperCase()
        .replace(/\s+/g, '_') === normalized
    );
  });
  return matchKey || normalized;
}

function getQualityStatusActionRequired(value) {
  const code = normalizeQualityStatusCode(value);
  const meta = QUALITY_STATUS_META[code];
  return (meta && meta.actionRequired) || 'No action guidance available.';
}

function buildVisibleTickSet(labels, maxTicks) {
  const unique = Array.from(new Set(labels.filter(Boolean)));
  if (unique.length <= maxTicks) return new Set(unique);
  const step = Math.ceil(unique.length / maxTicks);
  const visible = unique.filter((_, idx) => idx % step === 0 || idx === unique.length - 1);
  return new Set(visible);
}

function getQualityStatusColor(stateOrLabel) {
  const raw = stateOrLabel == null ? '' : String(stateOrLabel);
  const normalized = raw
    .trim()
    .toUpperCase()
    .replace(/\s+/g, '_');
  const keys = Object.keys(QUALITY_STATUS_META);
  const matchKey = keys.find(code => {
    if (code === normalized) return true;
    const meta = QUALITY_STATUS_META[code];
    if (!meta || !meta.label) return false;
    return (
      String(meta.label)
        .toUpperCase()
        .replace(/\s+/g, '_') === normalized
    );
  });
  const meta = matchKey ? QUALITY_STATUS_META[matchKey] : null;
  return (meta && meta.color) || 'gray';
}

/** Map API listing to table row shape used by Demo 4 UI */
function mapListingIssuesItems(itemsInput) {
  const items = Array.isArray(itemsInput) ? itemsInput : [];
  const byResId = {};
  items.forEach(it => {
    const rid = it.reservation_id || it.reservationId || it.confirmationCode || '—';
    if (!byResId[rid]) byResId[rid] = { reviewIssues: [], violations: [], reservationObjectId: null };
    const reservationObjectId = it._id || it.reservationObjectId || null;
    if (!byResId[rid].reservationObjectId && reservationObjectId) byResId[rid].reservationObjectId = String(reservationObjectId);
    if (it.type === 'CS_VIOLATION') {
      byResId[rid].violations.push(it.label || `${it.category || ''} ${it.tag || ''}`.trim() || 'Violation');
    } else {
      byResId[rid].reviewIssues.push(it.label || `${it.category || ''} ${it.tag || ''}`.trim() || 'Issue');
    }
  });
  const modalReviewIssues = Object.entries(byResId)
    .filter(([, v]) => v.reviewIssues.length > 0)
    .map(([reservationId, v]) => ({ reservationId, reservationObjectId: v.reservationObjectId, issues: v.reviewIssues }));
  const modalViolations = Object.entries(byResId)
    .filter(([, v]) => v.violations.length > 0)
    .map(([reservationId, v]) => ({ reservationId, reservationObjectId: v.reservationObjectId, violations: v.violations }));
  const issuesTooltip = Object.entries(byResId)
    .map(([rid, v]) => {
      const parts = [];
      if (v.reviewIssues.length) parts.push(...v.reviewIssues.map(i => `- ${i}`));
      if (v.violations.length) parts.push(...v.violations.map(i => `- ${i}`));
      return parts.length ? `Reservation ID: ${rid}\n${parts.join('\n')}` : '';
    })
    .filter(Boolean)
    .join('\n\n');
  return {
    modalReviewIssues,
    modalViolations,
    issuesTooltip: issuesTooltip || undefined
  };
}

function buildReservationReviewUrl(reservationObjectId) {
  if (!reservationObjectId) return null;
  return `https://system.hostplatform.com/reservation/${reservationObjectId}/edit?selectedTab=reviews`;
}

function mapApiListingToRow(listing) {
  const detail = listing.airbnb_issues_detail || {};
  const mappedIssues = mapListingIssuesItems(detail.items);
  const listingId = String(listing.id);
  const listingName = listing.name || listing.listing_nickname || listing.nickname || listingId;

  return {
    key: listingId,
    listingName,
    listing: `${listingId} - ${listingName}`,
    avgScore: listing.average_review_score && listing.average_review_score.value != null ? listing.average_review_score.value : '—',
    reviewCount: listing.average_review_score && listing.average_review_score.reviews_count != null ? listing.average_review_score.reviews_count : 0,
    qualityStatus: listing.quality_state_label || listing.quality_state || '—',
    qualityStatusColor: getQualityStatusColor(listing.quality_state || listing.quality_state_label),
    reviewIssues: listing.airbnb_review_issues_count != null ? listing.airbnb_review_issues_count : 0,
    csViolations: listing.airbnb_cs_violations_count != null ? listing.airbnb_cs_violations_count : 0,
    issuesTooltip: mappedIssues.issuesTooltip,
    modalReviewIssues: mappedIssues.modalReviewIssues,
    modalViolations: mappedIssues.modalViolations
  };
}

const componentEvolutionColors = {
  Cleanliness: '#13C2C2',
  Accuracy: '#FA8C16',
  Communication: '#595959',
  'Check-in': '#69C0FF',
  Location: '#FAAD14',
  Value: '#2F54EB'
};
const COMPONENT_EVOLUTION_FIELDS = ['Cleanliness', 'Accuracy', 'Communication', 'Check-in', 'Location', 'Value'];
const REVIEW_CATEGORY_UI_META = {
  cleanliness: { key: 'cleanliness', label: 'Cleanliness', icon: 'home' },
  arrival: { key: 'arrival', label: 'Arrival', icon: 'key' },
  accuracy: { key: 'accuracy', label: 'Accuracy', icon: 'check-circle' },
  communication: { key: 'communication', label: 'Communication', icon: 'message' },
  location: { key: 'location', label: 'Location', icon: 'environment' },
  quality: { key: 'quality', label: 'Quality', icon: 'tag' }
};

function toReviewUiCategoryKey(rawCategory) {
  const raw = String(rawCategory == null ? '' : rawCategory)
    .trim()
    .toLowerCase()
    .replace(/[-_]/g, ' ');
  if (!raw) return null;
  if (raw.includes('clean')) return 'cleanliness';
  if (raw.includes('accur')) return 'accuracy';
  if (raw.includes('communic')) return 'communication';
  if (raw.includes('check') || raw.includes('arrival')) return 'arrival';
  if (raw.includes('locat')) return 'location';
  if (raw.includes('value') || raw.includes('quality')) return 'quality';
  return null;
}

function pickFirstNumber(...candidates) {
  for (let i = 0; i < candidates.length; i++) {
    const v = candidates[i];
    if (v == null || v === '') continue;
    const n = Number(v);
    if (!Number.isNaN(n)) return n;
  }
  return null;
}

function periodLabelFromRow(row, index) {
  if (row == null) return 'W' + (index + 1);
  if (row.period_key != null) return String(row.period_key);
  if (row.periodKey != null) return String(row.periodKey);
  if (row.bucket != null) return String(row.bucket);
  if (row.week != null) return String(row.week);
  return 'W' + (index + 1);
}

function hasCompletePeriodFlag(rows) {
  return rows.some(row => row && (row.is_complete_period != null || row.is_complete != null || row.isComplete != null));
}

function isCompletePeriodRow(row, index, hasCompleteFlag, rowCount) {
  const rawComplete = row.is_complete_period != null ? row.is_complete_period : row.is_complete != null ? row.is_complete : row.isComplete;
  if (hasCompleteFlag && rawComplete != null) return Boolean(rawComplete);
  return !hasCompleteFlag ? index !== rowCount - 1 : true;
}

function buildTrendDeltaCardFromSeries(completeRows, valueKey, yoyKey) {
  const pointsWithValues = completeRows.filter(point => pickFirstNumber(point[valueKey]) != null);
  if (!pointsWithValues.length) return { latest: { [valueKey]: null }, wowDelta: null, yoyDelta: null };

  const latest = pointsWithValues[pointsWithValues.length - 1];
  const prev = pointsWithValues.length > 1 ? pointsWithValues[pointsWithValues.length - 2] : null;
  const latestVal = latest[valueKey];
  const prevVal = prev ? prev[valueKey] : null;

  return {
    latest,
    wowDelta: prevVal != null ? latestVal - prevVal : null,
    yoyDelta: latest[yoyKey] != null ? latestVal - latest[yoyKey] : null
  };
}

/** Map API review-component name to chart field (Airbnb-style names). */
function mapReviewCategoryToComponentKey(name) {
  const raw = String(name == null ? '' : name)
    .trim()
    .toLowerCase()
    .replace(/_/g, ' ');
  if (!raw) return null;
  if (raw.includes('clean')) return 'Cleanliness';
  if (raw.includes('accur')) return 'Accuracy';
  if (raw.includes('communic')) return 'Communication';
  if (raw.includes('check')) return 'Check-in';
  if (raw.includes('locat')) return 'Location';
  if (raw.includes('value')) return 'Value';
  return null;
}

function formatToTwoDecimals(value) {
  const n = pickFirstNumber(value);
  if (n == null) return '—';
  return Number(n).toFixed(2);
}

function formatSignedDelta(value, decimals = 2) {
  const n = pickFirstNumber(value);
  if (n == null) return '—';
  const num = Number(n);
  const sign = num >= 0 ? '+' : '-';
  return `${sign}${Math.abs(num).toFixed(decimals)}`;
}

/** Parse component evolution `categories: [...]` rows from the API. */
function componentScoresFromCategories(categories) {
  const out = {
    Cleanliness: null,
    Accuracy: null,
    Communication: null,
    'Check-in': null,
    Location: null,
    Value: null
  };
  if (!Array.isArray(categories)) return out;
  categories.forEach(item => {
    if (item == null || typeof item !== 'object') return;
    const name =
      item.category != null
        ? item.category
        : item.name != null
        ? item.name
        : item.dimension != null
        ? item.dimension
        : item.key != null
        ? item.key
        : item.label != null
        ? item.label
        : item.type;
    const chartKey = mapReviewCategoryToComponentKey(name);
    if (!chartKey) return;
    const val = pickFirstNumber(
      item.avg_rating,
      item.average_rating,
      item.avg,
      item.score,
      item.rating,
      item.value,
      item.mean,
      item.avg_component_rating
    );
    if (val != null) out[chartKey] = val;
  });
  return out;
}

const QualityDashboardDemo8 = props => {
  const { getFieldDecorator, setFieldsValue, validateFields, getFieldValue } = props.form;
  const [activeTab, setActiveTab] = useState('listings');
  const [granularity, setGranularity] = useState('monthly');
  const [lookback, setLookback] = useState('6');
  const [dateRange, setDateRange] = useState(null);
  const [listingFilter, setListingFilter] = useState('');
  const [countryFilter, setCountryFilter] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [qualityStatusFilter, setQualityStatusFilter] = useState('ALL');
  const [kpiStatusDrilldown, setKpiStatusDrilldown] = useState('ALL');
  const [filtersVisible, setFiltersVisible] = useState(false);
  const [issuesModalVisible, setIssuesModalVisible] = useState(false);
  const [issuesModalLoading, setIssuesModalLoading] = useState(false);
  const [selectedListingForModal, setSelectedListingForModal] = useState(null);
  const [showReviewTags, setShowReviewTags] = useState(false);
  const [selectedAirbnbUserId, setSelectedAirbnbUserId] = useState(null);
  const [airbnbAccounts, setAirbnbAccounts] = useState([]);
  const [airbnbAccountsLoading, setAirbnbAccountsLoading] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const [listingsQuery, setListingsQuery] = useState({ currentPage: 1, limit: 10 });
  const [countriesLoading, setCountriesLoading] = useState(false);
  const [countries, setCountries] = useState([]);
  const [statesLoading, setStatesLoading] = useState(false);
  const [statesList, setStatesList] = useState([]);
  const [modalCountryCode, setModalCountryCode] = useState(null);

  /** MY state dropdown list: use applied country when modal closed, else modal selection (fallback to applied). */
  const stateListCountryIso = filtersVisible ? (modalCountryCode != null ? modalCountryCode : countryFilter) : countryFilter;

  const [error, setError] = useState(null);
  const [trendsData, setTrendsData] = useState(null);
  const [trendsLoading, setTrendsLoading] = useState(true);
  const [componentEvolutionApiData, setComponentEvolutionApiData] = useState(null);
  const [componentEvolutionLoading, setComponentEvolutionLoading] = useState(true);
  const [reviewSummaryData, setReviewSummaryData] = useState(null);
  const [reviewSummaryLoading, setReviewSummaryLoading] = useState(false);
  const [reviewTagsData, setReviewTagsData] = useState(null);
  const [reviewTagsLoading, setReviewTagsLoading] = useState(false);
  const [reviewViolationsData, setReviewViolationsData] = useState(null);
  const [reviewViolationsLoading, setReviewViolationsLoading] = useState(false);
  const [reviewAccommodationData, setReviewAccommodationData] = useState(null);
  const [reviewAccommodationLoading, setReviewAccommodationLoading] = useState(false);
  const listingsSectionRef = useRef(null);
  const [apiData, setApiData] = useState({
    total_listings: 0,
    metrics: {
      good: { count: 0, total: 0, percentage: 0, label: '' },
      warning: { count: 0, total: 0, percentage: 0, label: '' },
      at_risk: { count: 0, total: 0, percentage: 0, label: '' },
      suspended: { count: 0, total: 0, percentage: 0, label: '' }
    },
    listings: [],
    pagination: { total: 0, limit: 10 }
  });
  const [taskStatusFilter, setTaskStatusFilter] = useState('ALL');
  const [taskPriorityFilter, setTaskPriorityFilter] = useState('ALL');
  const [hostNotifications, setHostNotifications] = useState(HOST_NOTIFICATIONS_SEED);

  // Load available Airbnb accounts once, then default to the first option.
  useEffect(() => {
    let cancelled = false;
    setAirbnbAccountsLoading(true);
    setAirbnbAccounts([]);
    setSelectedAirbnbUserId(null);

    getAirbnbQualityDashboardAccounts()
      .then(res => {
        if (cancelled) return;
        const accounts = (res && res.data ? res.data : []) || [];
        setAirbnbAccounts(accounts);
        const first = accounts[0];
        if (first) {
          const id = first.airbnbUserId || first.airbnb_user_id || first._id || null;
          setSelectedAirbnbUserId(id != null ? String(id) : null);
        }
      })
      .catch(() => {
        if (!cancelled) {
          // Keep UI functional even if dropdown cannot be loaded.
          message.error('Failed to load Airbnb accounts');
        }
      })
      .finally(() => {
        if (!cancelled) setAirbnbAccountsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setCountriesLoading(true);
    getCountriesConstant()
      .then(res => {
        if (cancelled) return;
        const rows = Array.isArray(res) ? res : Object.values(res || {});
        const normalized = rows
          .map(item => {
            if (!item || typeof item !== 'object') {
              return null;
            }
            const code = item.iso2;
            const label = item.name;
            if (!code || !label) {
              return null;
            }
            return { code: String(code), label: String(label) };
          })
          .filter(Boolean)
          .sort((a, b) => a.label.localeCompare(b.label));
        setCountries(normalized);
      })
      .catch(err => console.log(err))
      .finally(() => {
        if (!cancelled) setCountriesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!filtersVisible) return;
    setModalCountryCode(countryFilter || null);
  }, [filtersVisible, countryFilter]);

  useEffect(() => {
    // Only fetch states for the country where we support a dropdown states list (same as e-invoice).
    if (!stateListCountryIso || String(stateListCountryIso) !== 'MY') {
      setStatesList([]);
      setStatesLoading(false);
      return;
    }

    let cancelled = false;
    setStatesLoading(true);
    setStatesList([]);
    getStatesConstant(stateListCountryIso)
      .then(res => {
        if (cancelled) return;
        const rows = Array.isArray(res) ? res : Object.values(res || {});
        setStatesList(rows);
      })
      .catch(err => console.log(err))
      .finally(() => {
        if (!cancelled) setStatesLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [stateListCountryIso]);

  // Backend expects state name (e.g. Pahang), not numeric code (e.g. 06); migrate legacy stored codes.
  useEffect(() => {
    if (String(countryFilter) !== 'MY' || !stateFilter.trim() || !statesList.length) return;
    const match = statesList.find(s => String(s.code) === String(stateFilter.trim()));
    if (match && match.label !== stateFilter.trim()) {
      setStateFilter(match.label);
    }
  }, [countryFilter, stateFilter, statesList]);

  const qualityDashboardPaginatedQuery = useMemo(() => {
    const q = {
      currentPage: listingsQuery.currentPage,
      limit: listingsQuery.limit,
      airbnbUserId: selectedAirbnbUserId || undefined,
      listing: listingFilter.trim() || undefined,
      city: cityFilter.trim() || undefined,
      country: countryFilter.trim() || undefined,
      state: stateFilter.trim() || undefined,
      quality_status: mapQualityStatusFilterToQueryValue(qualityStatusFilter),
      kpiStatus: kpiStatusDrilldown && kpiStatusDrilldown !== 'ALL' ? kpiStatusDrilldown : undefined
    };
    if (listingsQuery.filter) q.filter = listingsQuery.filter;
    if (listingsQuery.sorter) q.sorter = listingsQuery.sorter;
    if (dateRange && dateRange[0] && dateRange[1]) {
      q.startDate = moment(dateRange[0]).format('YYYY-MM-DD');
      q.endDate = moment(dateRange[1]).format('YYYY-MM-DD');
    }
    Object.keys(q).forEach(k => q[k] === undefined && delete q[k]);
    return q;
  }, [
    listingsQuery,
    selectedAirbnbUserId,
    listingFilter,
    cityFilter,
    countryFilter,
    stateFilter,
    qualityStatusFilter,
    kpiStatusDrilldown,
    dateRange
  ]);

  const {
    isLoading: dashboardLoading,
    paginatedData,
    total: dashboardTotal,
    latestData,
    error: dashboardQueryError
  } = useGetPaginatedAirbnbQualityDashboard(qualityDashboardPaginatedQuery);

  useEffect(() => {
    if (!latestData) return;
    const defaultMetrics = { good: {}, warning: {}, at_risk: {}, suspended: {} };
    setApiData({
      total_listings: latestData.total_listings != null ? latestData.total_listings : 0,
      metrics: latestData.metrics || defaultMetrics,
      listings: [],
      pagination: { total: latestData.totalCount != null ? latestData.totalCount : 0, limit: listingsQuery.limit }
    });
  }, [latestData, listingsQuery.limit]);

  useEffect(() => {
    if (!dashboardQueryError) {
      setError(null);
      return;
    }
    const msg =
      (dashboardQueryError.response && dashboardQueryError.response.data && dashboardQueryError.response.data.message) ||
      dashboardQueryError.message ||
      'Failed to load quality dashboard';
    setError(msg);
    message.error(msg);
  }, [dashboardQueryError]);

  // For long ranges, monthly is usually the readable default.
  useEffect(() => {
    if (lookback === '12' || lookback === '24' || lookback === 'all') {
      setGranularity(prev => (prev === 'weekly' ? 'monthly' : prev));
    }
  }, [lookback]);

  // Build start/end for trends and component evolution (backend expects startDate, endDate)
  const trendsDateRange = useMemo(() => {
    const end = dateRange && dateRange[1] ? moment(dateRange[1]) : moment();
    let start;
    if (dateRange && dateRange[0]) {
      start = moment(dateRange[0]);
    } else {
      const months = lookback === 'all' ? 24 : Math.max(1, parseInt(lookback, 10) || 3);
      start = moment(end).subtract(months, 'months');
    }
    return { startDate: start.format('YYYY-MM-DD'), endDate: end.format('YYYY-MM-DD') };
  }, [dateRange, lookback]);

  useEffect(() => {
    let cancelled = false;
    if (!selectedAirbnbUserId) {
      setTrendsData(null);
      setTrendsLoading(false);
      return () => {
        cancelled = true;
      };
    }
    setTrendsLoading(true);
    const base = buildSharedQualityDashboardFilters({
      listingFilter,
      cityFilter,
      countryFilter,
      stateFilter,
      qualityStatusFilter,
      kpiStatusDrilldown,
      selectedAirbnbUserId,
      startDate: trendsDateRange.startDate,
      endDate: trendsDateRange.endDate
    });
    const query = {
      ...base,
      granularity: granularity === 'weekly' ? 'week' : 'month'
    };
    Object.keys(query).forEach(k => query[k] === undefined && delete query[k]);
    getReviewTrends(query)
      .then(res => {
        if (cancelled) return;
        const payload = res && res.data ? res.data : null;
        setTrendsData(payload);
      })
      .catch(() => {
        if (!cancelled) setTrendsData(null);
      })
      .finally(() => {
        if (!cancelled) setTrendsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    listingFilter,
    cityFilter,
    countryFilter,
    stateFilter,
    qualityStatusFilter,
    kpiStatusDrilldown,
    trendsDateRange.startDate,
    trendsDateRange.endDate,
    granularity,
    selectedAirbnbUserId
  ]);

  useEffect(() => {
    let cancelled = false;
    if (!selectedAirbnbUserId) {
      setComponentEvolutionApiData(null);
      setComponentEvolutionLoading(false);
      return () => {
        cancelled = true;
      };
    }
    setComponentEvolutionLoading(true);
    const base = buildSharedQualityDashboardFilters({
      listingFilter,
      cityFilter,
      countryFilter,
      stateFilter,
      qualityStatusFilter,
      kpiStatusDrilldown,
      selectedAirbnbUserId,
      startDate: trendsDateRange.startDate,
      endDate: trendsDateRange.endDate
    });
    const query = {
      ...base,
      granularity: granularity === 'weekly' ? 'week' : 'month'
    };
    Object.keys(query).forEach(k => query[k] === undefined && delete query[k]);
    getComponentEvolution(query)
      .then(res => {
        if (cancelled) return;
        const payload = res && res.data ? res.data : null;
        setComponentEvolutionApiData(payload);
      })
      .catch(() => {
        if (!cancelled) setComponentEvolutionApiData(null);
      })
      .finally(() => {
        if (!cancelled) setComponentEvolutionLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    listingFilter,
    cityFilter,
    countryFilter,
    stateFilter,
    qualityStatusFilter,
    kpiStatusDrilldown,
    trendsDateRange.startDate,
    trendsDateRange.endDate,
    granularity,
    selectedAirbnbUserId
  ]);

  const reviewQualityQuery = useMemo(() => {
    if (!selectedAirbnbUserId) return null;
    return buildSharedQualityDashboardFilters({
      listingFilter,
      cityFilter,
      countryFilter,
      stateFilter,
      qualityStatusFilter,
      kpiStatusDrilldown,
      selectedAirbnbUserId,
      startDate: trendsDateRange.startDate,
      endDate: trendsDateRange.endDate
    });
  }, [
    listingFilter,
    cityFilter,
    countryFilter,
    stateFilter,
    qualityStatusFilter,
    kpiStatusDrilldown,
    trendsDateRange.startDate,
    trendsDateRange.endDate,
    selectedAirbnbUserId
  ]);

  useEffect(() => {
    let cancelled = false;
    setReviewSummaryLoading(true);
    if (!reviewQualityQuery) {
      setReviewSummaryData(null);
      setReviewSummaryLoading(false);
      return () => {
        cancelled = true;
      };
    }
    const query = { ...reviewQualityQuery };
    Object.keys(query).forEach(k => query[k] === undefined && delete query[k]);
    getReviewQualitySummary(query)
      .then(res => {
        if (cancelled) return;
        setReviewSummaryData(res && res.data ? res.data : null);
      })
      .catch(() => {
        if (!cancelled) setReviewSummaryData(null);
      })
      .finally(() => {
        if (!cancelled) setReviewSummaryLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reviewQualityQuery]);

  useEffect(() => {
    let cancelled = false;
    setReviewViolationsLoading(true);
    if (!reviewQualityQuery) {
      setReviewViolationsData(null);
      setReviewViolationsLoading(false);
      return () => {
        cancelled = true;
      };
    }
    const query = { ...reviewQualityQuery };
    Object.keys(query).forEach(k => query[k] === undefined && delete query[k]);
    getReviewQualityViolations(query)
      .then(res => {
        if (cancelled) return;
        setReviewViolationsData(res && res.data ? res.data : null);
      })
      .catch(() => {
        if (!cancelled) setReviewViolationsData(null);
      })
      .finally(() => {
        if (!cancelled) setReviewViolationsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reviewQualityQuery]);

  useEffect(() => {
    let cancelled = false;
    setReviewAccommodationLoading(true);
    if (!reviewQualityQuery) {
      setReviewAccommodationData(null);
      setReviewAccommodationLoading(false);
      return () => {
        cancelled = true;
      };
    }
    const query = { ...reviewQualityQuery };
    Object.keys(query).forEach(k => query[k] === undefined && delete query[k]);
    getReviewQualityAccommodationStatus(query)
      .then(res => {
        if (cancelled) return;
        setReviewAccommodationData(res && res.data ? res.data : null);
      })
      .catch(() => {
        if (!cancelled) setReviewAccommodationData(null);
      })
      .finally(() => {
        if (!cancelled) setReviewAccommodationLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reviewQualityQuery]);

  useEffect(() => {
    if (activeTab !== 'reviews' || !showReviewTags) return;
    let cancelled = false;
    setReviewTagsLoading(true);
    if (!reviewQualityQuery) {
      setReviewTagsData(null);
      setReviewTagsLoading(false);
      return () => {
        cancelled = true;
      };
    }
    const query = { ...reviewQualityQuery };
    Object.keys(query).forEach(k => query[k] === undefined && delete query[k]);
    getReviewQualityTags(query)
      .then(res => {
        if (cancelled) return;
        setReviewTagsData(res && res.data ? res.data : null);
      })
      .catch(() => {
        if (!cancelled) setReviewTagsData(null);
      })
      .finally(() => {
        if (!cancelled) setReviewTagsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activeTab, showReviewTags, reviewQualityQuery]);

  const clearFilters = () => {
    setDateRange(null);
    setListingFilter('');
    setCountryFilter('');
    setStateFilter('');
    setCityFilter('');
    setQualityStatusFilter('ALL');
    setKpiStatusDrilldown('ALL');
    setListingsQuery({ currentPage: 1, limit: 10 });
    if (filtersVisible) {
      setFieldsValue({
        dateRange: null,
        listing: undefined,
        country: undefined,
        state: undefined,
        city: undefined,
        qualityStatus: 'ALL'
      });
    }
  };

  const removeFilter = key => {
    if (key === 'dateRange') setDateRange(null);
    if (key === 'listing') setListingFilter('');
    if (key === 'country') {
      setCountryFilter('');
      setStateFilter('');
      setCityFilter('');
    }
    if (key === 'state') setStateFilter('');
    if (key === 'city') setCityFilter('');
    if (key === 'qualityStatus') setQualityStatusFilter('ALL');
    if (key === 'kpiStatus') setKpiStatusDrilldown('ALL');
    setListingsQuery(prev => ({ ...prev, currentPage: 1 }));
  };

  const handleKpiCardClick = statusFilterValue => {
    setKpiStatusDrilldown(statusFilterValue);
    setListingsQuery(prev => ({ ...prev, currentPage: 1 }));
    if (listingsSectionRef.current && typeof listingsSectionRef.current.scrollIntoView === 'function') {
      listingsSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleDownloadReport = async () => {
    try {
      if (reportLoading) return;
      setReportLoading(true);

      const headers = [
        'Date of Reception',
        'Host ID / Name',
        'Listing ID / Name',
        'Airbnb Reservation ID',
        'Guest Name',
        'Public Review',
        'Private Review',
        'Overall Rating',
        'Location Rating',
        'Comm Rating',
        'Value Rating',
        'Check in Rating',
        'Accuracy Rating',
        'Cleanliness Rating',
        'Negative Tags',
        'Quality Standard Status'
      ];

      const escapeCell = v => {
        if (v == null) return '';
        const s = String(v);
        // Quote if the value contains CSV-sensitive characters.
        if (/[",\n]/.test(s) || s.indexOf(',') >= 0) return `"${s.replace(/"/g, '""')}"`;
        return s;
      };

      const downloadCsv = rows => {
        const csvRows = (rows || []).map(r => [
          escapeCell(r.dateOfReception),
          escapeCell(r.hostIdName),
          escapeCell(r.listingIdName),
          escapeCell(r.airbnbResId),
          escapeCell(r.guestName),
          escapeCell(r.publicReview),
          escapeCell(r.privateReview),
          escapeCell(r.overallRating),
          escapeCell(r.locationRating),
          escapeCell(r.commRating),
          escapeCell(r.valueRating),
          escapeCell(r.checkinRating),
          escapeCell(r.accuracyRating),
          escapeCell(r.cleanlinessRating),
          escapeCell(r.negativeTags),
          escapeCell(r.qualityStatus)
        ]);

        const csv = [headers.join(','), ...csvRows.map(row => row.join(','))].join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `quality_detailed_reviews_${moment().format('YYYY-MM-DD')}.csv`;
        link.click();
        URL.revokeObjectURL(link.href);
      };

      const query = buildSharedQualityDashboardFilters({
        listingFilter,
        cityFilter,
        countryFilter,
        stateFilter,
        qualityStatusFilter,
        kpiStatusDrilldown,
        selectedAirbnbUserId,
        startDate: trendsDateRange.startDate,
        endDate: trendsDateRange.endDate
      });
      Object.keys(query).forEach(k => query[k] === undefined && delete query[k]);

      const res = await getQualityDashboardDetailedReviewsReport(query);
      const rows = res && res.data ? res.data : [];
      downloadCsv(rows);
    } catch (err) {
      const msg = (err && err.response && err.response.data && err.response.data.message) || (err && err.message) || 'Failed to download report';
      message.error(msg);
    } finally {
      setReportLoading(false);
    }
  };

  const activeFilterTags = [];
  if (dateRange && dateRange[0] && dateRange[1]) {
    activeFilterTags.push({
      key: 'dateRange',
      label: `From: ${moment(dateRange[0]).format('YYYY-MM-DD')} To: ${moment(dateRange[1]).format('YYYY-MM-DD')}`
    });
  }
  if (qualityStatusFilter && qualityStatusFilter !== 'ALL') {
    const label = mapQualityStatusFilterToTagLabel(qualityStatusFilter);
    activeFilterTags.push({ key: 'qualityStatus', label: `Status: ${label}` });
  }
  if (kpiStatusDrilldown && kpiStatusDrilldown !== 'ALL') {
    const label = (KPI_STATUS_META[kpiStatusDrilldown] && KPI_STATUS_META[kpiStatusDrilldown].label) || kpiStatusDrilldown;
    activeFilterTags.push({ key: 'kpiStatus', label: `KPI: ${label}` });
  }
  if (listingFilter.trim()) activeFilterTags.push({ key: 'listing', label: `Listing: ${listingFilter.trim()}` });
  if (countryFilter.trim()) activeFilterTags.push({ key: 'country', label: `Country: ${countryFilter.trim()}` });
  if (stateFilter.trim()) activeFilterTags.push({ key: 'state', label: `State: ${stateFilter.trim()}` });
  if (cityFilter.trim()) activeFilterTags.push({ key: 'city', label: `City: ${cityFilter.trim()}` });
  const hasActiveFilters = activeFilterTags.length > 0;

  useEffect(() => {
    window.dispatchEvent(new Event('resize'));
  }, [activeTab]);

  useEffect(() => {
    if (!filtersVisible) return;
    setFieldsValue({
      dateRange,
      listing: listingFilter || undefined,
      country: countryFilter || undefined,
      state: stateFilter || undefined,
      city: cityFilter || undefined,
      qualityStatus: mapQualityStatusFilterToModalValue(qualityStatusFilter)
    });
  }, [filtersVisible]);

  const handleApplyFilters = () => {
    validateFields((err, values) => {
      if (err) {
        return;
      }
      setDateRange(values.dateRange || null);
      setListingFilter(values.listing ? String(values.listing) : '');
      setCountryFilter(values.country ? String(values.country) : '');
      setStateFilter(values.country ? (values.state ? String(values.state) : '') : '');
      setCityFilter(values.city ? String(values.city) : '');
      setQualityStatusFilter(values.qualityStatus || 'ALL');
      setKpiStatusDrilldown('ALL');
      setListingsQuery(prev => ({ ...prev, currentPage: 1 }));
      setFiltersVisible(false);
    });
  };

  const listingsRows = useMemo(() => (Array.isArray(paginatedData) ? paginatedData : []).map(mapApiListingToRow), [paginatedData]);

  const listingLabelById = useMemo(() => {
    const rows = Array.isArray(paginatedData) ? paginatedData : [];
    const map = {};
    rows.forEach(listing => {
      const label = listing.name || listing.listing_nickname || listing.nickname;
      const ids = [listing.airbnb_listing_id, listing.airbnbListingId, listing.listing_id, listing.listingId, listing.id];
      ids.forEach(id => {
        if (id != null && String(id) !== '') map[String(id)] = label || String(id);
      });
    });
    return map;
  }, [paginatedData]);

  const qualityStatsFilterQuery = useMemo(() => {
    if (!selectedAirbnbUserId) return null;
    return buildSharedQualityDashboardFilters({
      listingFilter,
      cityFilter,
      countryFilter,
      stateFilter,
      qualityStatusFilter,
      kpiStatusDrilldown,
      selectedAirbnbUserId
    });
  }, [listingFilter, cityFilter, countryFilter, stateFilter, qualityStatusFilter, kpiStatusDrilldown, selectedAirbnbUserId]);

  const listingsPaginationTotal = useMemo(() => {
    if (dashboardTotal != null && Number(dashboardTotal) > 0) return Number(dashboardTotal);
    if (latestData && latestData.totalCount != null && Number(latestData.totalCount) > 0) return Number(latestData.totalCount);
    if (apiData && apiData.pagination && apiData.pagination.total != null && Number(apiData.pagination.total) > 0) {
      return Number(apiData.pagination.total);
    }
    return listingsRows.length;
  }, [dashboardTotal, latestData, apiData, listingsRows.length]);
  const listingQualityStatusFilterOptions = useMemo(() => {
    // Always show the full canonical status set (not just values present in the current page).
    // Note: AntD column filter matching is done against `record.qualityStatus`,
    // which is derived from `listing.quality_state_label || listing.quality_state`.
    // For immediate compatibility, we set radio values for suspended states to their canonical codes.
    const opts = [];

    const nonSuspended = ['GOOD', 'EDUCATE', 'WARN', 'PROBATION', 'ADDITIONAL_WARN', 'PENDING_REMOVAL'];
    nonSuspended.forEach(key => {
      const item = QUALITY_STATUS_META[key];
      if (!item) {
        return;
      }
      opts.push({ label: item.label, value: item.label });
    });

    const suspended = ['SUSPEND', 'REMOVED'];
    suspended.forEach(key => {
      const item = QUALITY_STATUS_META[key];
      if (!item) {
        return;
      }
      // Show a single option per status to avoid duplicate labels in the radio filter.
      // Backend is expected to provide `quality_state_label` so `record.qualityStatus`
      // matches `value === item.label` (see `airbnb-quality-status-label-contract.md`).
      opts.push({ label: item.label, value: item.label });
    });

    return opts;
  }, []);
  const reviewOverallRating = useMemo(() => {
    const v = pickFirstNumber(reviewSummaryData && reviewSummaryData.overall && reviewSummaryData.overall.avg_rating);
    return v != null ? v : null;
  }, [reviewSummaryData]);
  const reviewTotalCount = useMemo(() => {
    const overall = reviewSummaryData && reviewSummaryData.overall ? reviewSummaryData.overall : {};
    const total = pickFirstNumber(
      overall.review_count,
      overall.reviews_count,
      overall.total_reviews,
      reviewSummaryData && reviewSummaryData.review_count,
      reviewSummaryData && reviewSummaryData.reviews_count,
      reviewSummaryData && reviewSummaryData.total_reviews
    );
    return total != null ? Math.max(0, Math.round(total)) : 0;
  }, [reviewSummaryData]);
  const reviewCategoryCards = useMemo(() => {
    const rows = reviewSummaryData && Array.isArray(reviewSummaryData.categories) ? reviewSummaryData.categories : null;
    if (!rows || rows.length === 0) return [];
    const mapped = rows
      .map(row => {
        const key = toReviewUiCategoryKey(row.category);
        if (!key) return null;
        const meta = REVIEW_CATEGORY_UI_META[key];
        const posPct = pickFirstNumber(row.positive_percentage);
        const negPct = pickFirstNumber(row.negative_percentage);
        const p = posPct != null ? Math.round(posPct) : 0;
        const n = negPct != null ? Math.round(negPct) : Math.max(0, 100 - p);
        return {
          key: meta.key,
          label: meta.label,
          icon: meta.icon,
          rating: pickFirstNumber(row.avg_rating) || 0,
          greenPct: p,
          redPct: n
        };
      })
      .filter(Boolean);
    if (mapped.length === 0) return [];
    return Object.keys(REVIEW_CATEGORY_UI_META)
      .map(code => mapped.find(item => item.key === code))
      .filter(Boolean);
  }, [reviewSummaryData]);
  const reviewTagsByCategoryMapped = useMemo(() => {
    const rows = reviewTagsData && Array.isArray(reviewTagsData.categories) ? reviewTagsData.categories : null;
    if (!rows || rows.length === 0) return {};
    const out = {};
    rows.forEach(row => {
      const key = toReviewUiCategoryKey(row.category);
      if (!key) return;
      out[key] = {
        total: pickFirstNumber(row.total_count) || 0,
        positive: Array.isArray(row.positive)
          ? row.positive.map(i => ({ tag: i.label || i.tag || 'Tag', count: pickFirstNumber(i.count) || 0 }))
          : [],
        negative: Array.isArray(row.negative) ? row.negative.map(i => ({ tag: i.label || i.tag || 'Tag', count: pickFirstNumber(i.count) || 0 })) : []
      };
    });
    return out;
  }, [reviewTagsData]);
  const reviewViolationsMapped = useMemo(() => {
    const rows = reviewViolationsData && Array.isArray(reviewViolationsData.categories) ? reviewViolationsData.categories : null;
    if (!rows || rows.length === 0) return [];
    const list = rows
      .map(row => {
        const key = toReviewUiCategoryKey(row.category);
        if (!key) return null;
        const meta = REVIEW_CATEGORY_UI_META[key];
        return {
          label: row.label || meta.label,
          icon: meta.icon,
          count: pickFirstNumber(row.count) || 0
        };
      })
      .filter(Boolean);
    return list;
  }, [reviewViolationsData]);
  const reviewViolationsTotal = useMemo(() => {
    const total = pickFirstNumber(reviewViolationsData && reviewViolationsData.total_violations);
    if (total != null) return total;
    return reviewViolationsMapped.reduce((sum, row) => sum + (pickFirstNumber(row.count) || 0), 0);
  }, [reviewViolationsData, reviewViolationsMapped]);
  const accommodationStatusesMapped = useMemo(() => {
    const rows = reviewAccommodationData && Array.isArray(reviewAccommodationData.statuses) ? reviewAccommodationData.statuses : null;
    if (!rows || rows.length === 0) return [];
    const mapped = rows.map(row => ({
      label: row.label || row.state || 'Unknown',
      count: pickFirstNumber(row.count) || 0,
      color: getQualityStatusColor(row.state || row.label)
    }));
    return mapped;
  }, [reviewAccommodationData]);
  const metrics = apiData.metrics || {};
  const good = metrics.good || {};
  const warning = metrics.warning || {};
  const atRisk = metrics.at_risk || {};
  const suspended = metrics.suspended || {};
  const totalListings = apiData.total_listings != null ? apiData.total_listings : good.total || warning.total || atRisk.total || suspended.total || 0;

  const listingsColumns = [
    {
      ...constructColumn('Listing name', 'listing'),
      ...constructColumnFilterSearch('listing', 'Search Listing ID or Name'),
      width: 300,
      render: (_, r) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span>
            <span style={{ fontWeight: 600 }}>{r.key}</span>
            <span>{` - ${r.listingName}`}</span>
          </span>
        </div>
      )
    },
    {
      title: 'Average review score',
      dataIndex: 'avgScore',
      key: 'avgScore',
      align: 'center',
      width: 160,
      sorter: (a, b) => {
        const av = pickFirstNumber(a.avgScore);
        const bv = pickFirstNumber(b.avgScore);
        return (av != null ? av : -1) - (bv != null ? bv : -1);
      },
      render: (_, r) => (
        <span>
          {formatToTwoDecimals(r.avgScore)} ({r.reviewCount})
        </span>
      )
    },
    {
      title: 'Quality status',
      ...constructColumnFilterRadio('qualityStatus', listingQualityStatusFilterOptions),
      dataIndex: 'qualityStatus',
      key: 'qualityStatus',
      align: 'center',
      width: 160,
      render: (_, r) => (
        <Tooltip title={getQualityStatusActionRequired(r.qualityStatus)}>
          <Tag color={r.qualityStatusColor}>{r.qualityStatus}</Tag>
        </Tooltip>
      )
    },
    {
      title: 'Recent review issues',
      dataIndex: 'reviewIssues',
      key: 'reviewIssues',
      align: 'center',
      width: 140,
      sorter: (a, b) => (pickFirstNumber(a.reviewIssues) || 0) - (pickFirstNumber(b.reviewIssues) || 0),
      render: (val, record) =>
        record.issuesTooltip ? (
          <Tooltip title={<pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: 12 }}>{record.issuesTooltip}</pre>}>
            <span style={{ cursor: 'pointer', textDecoration: 'underline' }}>{val}</span>
          </Tooltip>
        ) : (
          val
        )
    },
    {
      title: 'Recent CS violations',
      dataIndex: 'csViolations',
      key: 'csViolations',
      align: 'center',
      width: 140,
      sorter: (a, b) => (pickFirstNumber(a.csViolations) || 0) - (pickFirstNumber(b.csViolations) || 0)
    },
    {
      title: '',
      key: 'action',
      width: 100,
      render: (_, record) => (
        <Button
          type="link"
          size="small"
          style={{ padding: 0 }}
          onClick={async () => {
            setSelectedListingForModal(record);
            setIssuesModalVisible(true);
            setIssuesModalLoading(true);
            try {
              const query = {};
              Object.keys(query).forEach(k => query[k] === undefined && delete query[k]);
              const res = await getQualityDashboardListingIssues(record.key, query);
              const payload = res && res.data ? res.data : null;
              const mappedIssues = mapListingIssuesItems(Array.isArray(payload) ? payload : payload && payload.items);
              setSelectedListingForModal(prev => ({
                ...(prev || record),
                modalReviewIssues: mappedIssues.modalReviewIssues,
                modalViolations: mappedIssues.modalViolations,
                issuesTooltip: mappedIssues.issuesTooltip
              }));
            } catch (err) {
              const msg =
                (err && err.response && err.response.data && err.response.data.message) || (err && err.message) || 'Failed to load listing issues';
              message.error(msg);
            } finally {
              setIssuesModalLoading(false);
            }
          }}
        >
          Read more
        </Button>
      )
    }
  ];

  const maxViolation = Math.max(...reviewViolationsMapped.map(v => v.count), 1);
  const accommodationPieData = accommodationStatusesMapped.filter(s => s.count > 0).map(s => ({ type: s.label, count: s.count, color: s.color }));

  const { DataView } = DataSet;
  const accommodationDv = new DataView();
  accommodationDv.source(accommodationPieData).transform({
    type: 'percent',
    field: 'count',
    dimension: 'type',
    as: 'percent'
  });
  const accommodationCols = {
    percent: {
      formatter: val => (Number(val) * 100).toFixed(2) + '%'
    }
  };

  // Map API review trends: period_key, avg_overall_rating, optional YoY / five-star fields
  const scoreChartData = useMemo(() => {
    const raw = trendsData && trendsData.series && Array.isArray(trendsData.series) ? trendsData.series : null;
    if (!raw || raw.length === 0) return [];
    const hasCompleteFlag = hasCompletePeriodFlag(raw);
    return raw.map((row, i) => {
      const week = periodLabelFromRow(row, i);
      const score = pickFirstNumber(row.avg_overall_rating, row.average_overall_rating, row.avg_review_score, row.score);
      const scoreYoY = pickFirstNumber(row.yoy_avg_overall_rating, row.avg_overall_rating_yoy, row.avg_review_score_yoy, row.score_yoy, row.scoreYoY);
      const isComplete = isCompletePeriodRow(row, i, hasCompleteFlag, raw.length);

      return {
        week,
        isComplete,
        segment: isComplete ? 'complete' : 'incomplete',
        score: score != null ? score : null,
        // For incomplete tail, hide YoY line by returning null.
        scoreYoY: isComplete ? (scoreYoY != null ? scoreYoY : null) : null
      };
    });
  }, [trendsData]);

  const fiveStarChartData = useMemo(() => {
    const raw = trendsData && trendsData.series && Array.isArray(trendsData.series) ? trendsData.series : null;
    if (!raw || raw.length === 0) return [];
    const hasCompleteFlag = hasCompletePeriodFlag(raw);
    return raw.map((row, i) => {
      const week = periodLabelFromRow(row, i);
      // Backend aggregate: five_star_percentage is 0–100; five_star_count / review_count is 0–1
      let rate = pickFirstNumber(
        row.five_star_percentage,
        row.fiveStarPercentage,
        row.five_star_rate,
        row.rate,
        row.pct_five_star,
        row.five_star_pct
      );
      const rc = pickFirstNumber(row.review_count, row.reviews_count);
      if (rate == null && rc != null && rc > 0) {
        const fsc = pickFirstNumber(row.five_star_count, row.fiveStarCount, row.count_five_star);
        if (fsc != null) rate = fsc / rc;
      }
      let rateYoY = pickFirstNumber(row.yoy_five_star_percentage, row.five_star_rate_yoy, row.rate_yoy, row.rateYoY);

      const isComplete = isCompletePeriodRow(row, i, hasCompleteFlag, raw.length);

      if (rate != null && rate > 1) rate = rate / 100;
      if (rateYoY != null && rateYoY > 1) rateYoY = rateYoY / 100;

      return {
        week,
        isComplete,
        segment: isComplete ? 'complete' : 'incomplete',
        rate: rate != null ? rate : null,
        // For incomplete tail, hide YoY line by returning null.
        rateYoY: isComplete ? (rateYoY != null ? rateYoY : null) : null
      };
    });
  }, [trendsData]);

  const componentEvolutionChartData = useMemo(() => {
    const raw =
      componentEvolutionApiData && componentEvolutionApiData.series && Array.isArray(componentEvolutionApiData.series)
        ? componentEvolutionApiData.series
        : null;
    if (!raw || raw.length === 0) return [];
    const hasCompleteFlag = hasCompletePeriodFlag(raw);
    const keyMap = {
      cleanliness: 'Cleanliness',
      accuracy: 'Accuracy',
      communication: 'Communication',
      check_in: 'Check-in',
      checkin: 'Check-in',
      checkIn: 'Check-in',
      location: 'Location',
      value: 'Value'
    };
    const fields = COMPONENT_EVOLUTION_FIELDS;
    return raw.map((row, i) => {
      const week = periodLabelFromRow(row, i);
      const isComplete = isCompletePeriodRow(row, i, hasCompleteFlag, raw.length);

      const fromCats = componentScoresFromCategories(row.categories);
      const fromCatsYoY = componentScoresFromCategories(
        row.yoy_categories || row.yoyCategories || row.yoy_cats || row.prior_year_categories || row.priorYearCategories
      );
      const out = { week };
      fields.forEach(cat => {
        const lowerUnderscore = cat.toLowerCase().replace(/-/g, '_');
        let val = pickFirstNumber(row[cat], row[lowerUnderscore], row[cat.toLowerCase()]);
        if (val == null) {
          const rowKey = Object.keys(row).find(k => (keyMap[k] || keyMap[k.toLowerCase()]) === cat);
          if (rowKey != null) val = pickFirstNumber(row[rowKey]);
        }
        if (val == null && fromCats[cat] != null) val = fromCats[cat];
        out[cat] = val != null ? val : null;

        // Keep YoY values as null when missing so card deltas don't show false zeros.
        let valYoY = pickFirstNumber(row[`${cat}YoY`], row[`${lowerUnderscore}YoY`], row[`${cat.toLowerCase()}_yoy`]);
        if (valYoY == null && fromCatsYoY[cat] != null) valYoY = fromCatsYoY[cat];
        out[`${cat}YoY`] = valYoY != null ? valYoY : null;
      });

      out.isComplete = isComplete;
      out.segment = isComplete ? 'complete' : 'incomplete';

      Object.keys(row).forEach(k => {
        const mapped = keyMap[k] || keyMap[k.toLowerCase()];
        if (mapped && out[mapped] == null) {
          const n = pickFirstNumber(row[k]);
          if (n != null) out[mapped] = n;
        }
      });
      return out;
    });
  }, [componentEvolutionApiData]);

  const componentEvolutionFields = COMPONENT_EVOLUTION_FIELDS;
  const scoreVisibleTicks = useMemo(() => buildVisibleTickSet(scoreChartData.map(d => d.week), 10), [scoreChartData]);
  const fiveStarVisibleTicks = useMemo(() => buildVisibleTickSet(fiveStarChartData.map(d => d.week), 10), [fiveStarChartData]);
  const componentVisibleTicks = useMemo(() => buildVisibleTickSet(componentEvolutionChartData.map(d => d.week), 10), [componentEvolutionChartData]);
  const scoreYoyNonNullCount = useMemo(() => scoreChartData.filter(d => d.scoreYoY != null).length, [scoreChartData]);
  const fiveStarYoyNonNullCount = useMemo(() => fiveStarChartData.filter(d => d.rateYoY != null).length, [fiveStarChartData]);
  const showScoreYoy = scoreYoyNonNullCount >= 3;
  const showFiveStarYoy = fiveStarYoyNonNullCount >= 3;
  const scoreFoldFields = showScoreYoy ? ['score', 'scoreYoY'] : ['score'];
  const fiveStarFoldFields = showFiveStarYoy ? ['rate', 'rateYoY'] : ['rate'];
  const showTrendPoints = (granularity === 'weekly' && scoreChartData.length <= 26) || (granularity === 'monthly' && scoreChartData.length <= 36);
  const showComponentPoints =
    (granularity === 'weekly' && componentEvolutionChartData.length <= 26) || (granularity === 'monthly' && componentEvolutionChartData.length <= 36);

  const scoreDeltaCard = useMemo(() => {
    const complete = scoreChartData.filter(d => d.isComplete);
    return buildTrendDeltaCardFromSeries(complete, 'score', 'scoreYoY');
  }, [scoreChartData]);

  const fiveStarDeltaCard = useMemo(() => {
    const complete = fiveStarChartData.filter(d => d.isComplete);
    return buildTrendDeltaCardFromSeries(complete, 'rate', 'rateYoY');
  }, [fiveStarChartData]);

  const componentDeltaCards = useMemo(() => {
    const complete = componentEvolutionChartData.filter(d => d.isComplete);
    return componentEvolutionFields.map(cat => {
      const pointsWithValues = complete.filter(point => pickFirstNumber(point[cat]) != null);
      if (!pointsWithValues.length) {
        return { cat, latestVal: null, wowDelta: null, yoyDelta: null };
      }

      const latestPoint = pointsWithValues[pointsWithValues.length - 1];
      const prevPoint = pointsWithValues.length > 1 ? pointsWithValues[pointsWithValues.length - 2] : null;

      const latestVal = latestPoint[cat];
      const prevVal = prevPoint ? prevPoint[cat] : null;
      const wowDelta = prevVal != null ? latestVal - prevVal : null;
      const yoyVal = latestPoint[`${cat}YoY`];
      const yoyDelta = yoyVal != null ? latestVal - yoyVal : null;
      return { cat, latestVal, wowDelta, yoyDelta };
    });
  }, [componentEvolutionChartData]);

  // Trends + Component Evolution transforms
  const dsScore = new DataSet();
  const dvScore = dsScore.createView().source(scoreChartData);
  dvScore
    .transform({ type: 'fold', fields: scoreFoldFields, key: 'type', value: 'value' })
    .transform({
      type: 'map',
      callback: row => ({
        ...row,
        type: row.type === 'score' ? 'This Period' : 'YoY'
      })
    })
    .transform({
      type: 'filter',
      callback: row => row && row.value != null
    });

  const dsFiveStar = new DataSet();
  const dvFiveStar = dsFiveStar.createView().source(fiveStarChartData);
  dvFiveStar
    .transform({ type: 'fold', fields: fiveStarFoldFields, key: 'type', value: 'value' })
    .transform({
      type: 'map',
      callback: row => ({
        ...row,
        type: row.type === 'rate' ? 'This Period' : 'YoY',
        value: row.value == null ? null : Number((row.value * 100).toFixed(1))
      })
    })
    .transform({
      type: 'filter',
      callback: row => row && row.value != null
    });

  const dsComponent = new DataSet();
  const dvComponent = dsComponent.createView().source(componentEvolutionChartData);
  dvComponent
    .transform({
      type: 'fold',
      fields: componentEvolutionFields,
      key: 'category',
      value: 'rate'
    })
    .transform({
      type: 'filter',
      callback: row => row && row.rate != null
    });

  const filteredCheckoutTasks = useMemo(
    () =>
      CHECKOUT_TASKS.filter(task => {
        const statusMatch = taskStatusFilter === 'ALL' || task.status === taskStatusFilter;
        const priorityMatch = taskPriorityFilter === 'ALL' || task.priority === taskPriorityFilter;
        return statusMatch && priorityMatch;
      }),
    [taskStatusFilter, taskPriorityFilter]
  );
  const checkoutKpis = useMemo(() => {
    const openTasks = CHECKOUT_TASKS.filter(t => t.status !== 'Done').length;
    const overdue = CHECKOUT_TASKS.filter(t => t.status === 'Overdue').length;
    const completedToday = CHECKOUT_TASKS.filter(t => t.status === 'Done').length;
    const doneWithDuration = CHECKOUT_TASKS.filter(t => t.status === 'Done' && t.completedMinutes != null);
    const avgCompletion =
      doneWithDuration.length > 0 ? Math.round(doneWithDuration.reduce((sum, item) => sum + item.completedMinutes, 0) / doneWithDuration.length) : 0;
    return { openTasks, overdue, completedToday, avgCompletion };
  }, []);
  const notificationsKpis = useMemo(() => {
    const unread = hostNotifications.filter(n => !n.read).length;
    const critical = hostNotifications.filter(n => n.severity === 'Critical').length;
    const warning = hostNotifications.filter(n => n.severity === 'Warning').length;
    const info = hostNotifications.filter(n => n.severity === 'Info').length;
    return { unread, critical, warning, info };
  }, [hostNotifications]);
  const groundRulesKpis = useMemo(() => {
    const triggered = GROUND_RULE_NOTIFICATIONS.length;
    const acknowledged = GROUND_RULE_NOTIFICATIONS.filter(r => r.state === 'Acknowledged').length;
    const escalated = GROUND_RULE_NOTIFICATIONS.filter(r => r.state === 'Escalated').length;
    const resolved = GROUND_RULE_NOTIFICATIONS.filter(r => r.state === 'Resolved').length;
    return { triggered, acknowledged, escalated, resolved };
  }, []);
  const checkoutTaskColumns = [
    { title: 'Task ID', dataIndex: 'id', key: 'id', width: 110 },
    { title: 'Listing', dataIndex: 'listing', key: 'listing' },
    { title: 'Reservation ID', dataIndex: 'reservationId', key: 'reservationId', width: 140 },
    { title: 'Guest', dataIndex: 'guest', key: 'guest', width: 120 },
    { title: 'Due At', dataIndex: 'dueAt', key: 'dueAt', width: 150 },
    { title: 'Assignee', dataIndex: 'assignee', key: 'assignee', width: 120 },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: value => <Tag color={STATUS_COLOR[value] || 'default'}>{value}</Tag>
    },
    { title: 'Priority', dataIndex: 'priority', key: 'priority', width: 120 }
  ];
  const groundRulesColumns = [
    { title: 'Rule', dataIndex: 'rule', key: 'rule', width: 220 },
    { title: 'Listing', dataIndex: 'listing', key: 'listing', width: 200 },
    { title: 'Reservation', dataIndex: 'reservationId', key: 'reservationId', width: 140 },
    { title: 'Trigger reason', dataIndex: 'triggerReason', key: 'triggerReason' },
    { title: 'Detected at', dataIndex: 'detectedAt', key: 'detectedAt', width: 160 },
    {
      title: 'Current state',
      dataIndex: 'state',
      key: 'state',
      width: 130,
      render: value => <Tag color={STATUS_COLOR[value] || 'default'}>{value}</Tag>
    }
  ];

  return (
    <StyledCard
      title={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <span>Airbnb Quality Dashboard (Demo 8 — Quality Statistics)</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <Button
              icon="filter"
              onClick={() => {
                setFiltersVisible(true);
              }}
            >
              Filters
              {hasActiveFilters && (
                <Tag color="blue" style={{ marginLeft: 6 }}>
                  {activeFilterTags.length}
                </Tag>
              )}
            </Button>
            <Select
              value={selectedAirbnbUserId || undefined}
              loading={airbnbAccountsLoading}
              style={{ width: 220 }}
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) => {
                const label = option && option.props ? option.props.children : '';
                return String(label)
                  .toLowerCase()
                  .includes(String(input || '').toLowerCase());
              }}
              onChange={value => {
                setSelectedAirbnbUserId(value != null ? String(value) : null);
                setListingsQuery(prev => ({ ...prev, currentPage: 1 }));
              }}
              disabled={airbnbAccountsLoading || airbnbAccounts.length === 0}
            >
              {airbnbAccounts.map(acc => {
                const id = acc.airbnbUserId || acc.airbnb_user_id || acc._id;
                if (id == null) return null;
                return (
                  <Option key={String(id)} value={String(id)}>
                    {acc.nickname || acc.airbnbUserId || acc.airbnb_user_id || String(id)}
                  </Option>
                );
              })}
            </Select>
          </div>
        </div>
      }
    >
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        tabBarExtraContent={
          <div style={{ paddingBottom: 6 }}>
            <Button type="primary" icon="download" loading={reportLoading} onClick={handleDownloadReport}>
              Download report
            </Button>
          </div>
        }
      >
        <Tabs.TabPane tab="Listings Quality" key="listings">
          {hasActiveFilters && (
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 8,
                marginBottom: 16,
                padding: '12px 0',
                borderBottom: '1px solid #f0f0f0'
              }}
            >
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
                {activeFilterTags.map(({ key, label }) => (
                  <Tag key={key} closable onClose={() => removeFilter(key)} color="blue">
                    {label}
                  </Tag>
                ))}
                <a onClick={clearFilters} style={{ fontSize: 13 }}>
                  Clear filters
                </a>
              </div>
              <div />
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
            <SectionTitle style={{ marginBottom: 0 }}>Metrics</SectionTitle>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}></div>
          </div>
          {error && (
            <div style={{ marginBottom: 16, padding: 12, background: '#fff2f0', border: '1px solid #ffccc7', borderRadius: 4, color: '#cf1322' }}>
              {error}
            </div>
          )}
          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            <Col xs={24} sm={12} md={6}>
              <KPICard loading={dashboardLoading} onClick={() => handleKpiCardClick(KPI_STATUS_FILTER_VALUE.GOOD)} style={{ cursor: 'pointer' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#666', marginBottom: 4 }}>
                  <span>Listings in "Good" status</span>
                  <Tooltip title={KPI_STATUS_META[KPI_STATUS_FILTER_VALUE.GOOD].tooltip}>
                    <Icon type="info-circle" style={{ color: '#8c8c8c', fontSize: 12 }} />
                  </Tooltip>
                </div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                  <span style={{ fontSize: 22, fontWeight: 600, color: '#52c41a' }}>
                    {good.count != null ? good.count : 0} / {totalListings}
                  </span>
                  <Icon type="arrow-up" style={{ color: '#1890ff', fontSize: 14 }} />
                </div>
              </KPICard>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <KPICard loading={dashboardLoading} onClick={() => handleKpiCardClick(KPI_STATUS_FILTER_VALUE.WARNING)} style={{ cursor: 'pointer' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#666', marginBottom: 4 }}>
                  <span>Listings in "Warning" status</span>
                  <Tooltip title={KPI_STATUS_META[KPI_STATUS_FILTER_VALUE.WARNING].tooltip}>
                    <Icon type="info-circle" style={{ color: '#8c8c8c', fontSize: 12 }} />
                  </Tooltip>
                </div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                  <span style={{ fontSize: 22, fontWeight: 600, color: '#fa8c16' }}>
                    {warning.count != null ? warning.count : 0} / {totalListings}
                  </span>
                  <Icon type="arrow-up" style={{ color: '#ff4d4f', fontSize: 14 }} />
                </div>
              </KPICard>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <KPICard loading={dashboardLoading} onClick={() => handleKpiCardClick(KPI_STATUS_FILTER_VALUE.AT_RISK)} style={{ cursor: 'pointer' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#666', marginBottom: 4 }}>
                  <span>Listings at risk</span>
                  <Tooltip title={KPI_STATUS_META[KPI_STATUS_FILTER_VALUE.AT_RISK].tooltip}>
                    <Icon type="info-circle" style={{ color: '#8c8c8c', fontSize: 12 }} />
                  </Tooltip>
                </div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                  <span style={{ fontSize: 22, fontWeight: 600, color: '#ff4d4f' }}>
                    {atRisk.count != null ? atRisk.count : 0} / {totalListings}
                  </span>
                  <Icon type="arrow-up" style={{ color: '#ff4d4f', fontSize: 14 }} />
                </div>
              </KPICard>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <KPICard loading={dashboardLoading} onClick={() => handleKpiCardClick(KPI_STATUS_FILTER_VALUE.SUSPENDED)} style={{ cursor: 'pointer' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#666', marginBottom: 4 }}>
                  <span>Suspended Listings</span>
                  <Tooltip title={KPI_STATUS_META[KPI_STATUS_FILTER_VALUE.SUSPENDED].tooltip}>
                    <Icon type="info-circle" style={{ color: '#8c8c8c', fontSize: 12 }} />
                  </Tooltip>
                </div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                  <span style={{ fontSize: 22, fontWeight: 600, color: '#262626' }}>
                    {suspended.count != null ? suspended.count : 0} / {totalListings}
                  </span>
                  <Icon type="arrow-up" style={{ color: '#262626', fontSize: 14 }} />
                </div>
              </KPICard>
            </Col>
          </Row>

          {/* Trends – filters + two charts side by side (copied from Demo 6) */}
          <SectionTitle style={{ marginBottom: 12 }}>Review Trends</SectionTitle>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 16, marginBottom: 20 }}>
            <span style={{ fontSize: 13, color: '#595959' }}>Granularity:</span>
            <Radio.Group
              value={granularity}
              onChange={e => setGranularity(e.target.value)}
              size="small"
              buttonStyle="solid"
              style={{ marginRight: 24 }}
            >
              <Radio.Button value="weekly">Weekly</Radio.Button>
              <Radio.Button value="monthly">Monthly</Radio.Button>
            </Radio.Group>
            <span style={{ fontSize: 13, color: '#595959' }}>Lookback:</span>
            <Select value={lookback} onChange={setLookback} style={{ width: 140 }} size="small">
              <Option value="1">1 month</Option>
              <Option value="3">3 months</Option>
              <Option value="6">6 months</Option>
              <Option value="12">1 year</Option>
              <Option value="24">2 years</Option>
              <Option value="all">All time</Option>
            </Select>
          </div>
          <Row gutter={[16, 16]} style={{ marginBottom: 32 }}>
            <Col xs={24} lg={12}>
              <Card
                title={
                  <span>
                    Average Review Score
                    <Tooltip title={TOOLTIP_COPY.trends.averageReviewScore}>
                      <Icon type="info-circle" style={{ marginLeft: 8, color: '#8c8c8c' }} />
                    </Tooltip>
                  </span>
                }
                size="small"
                style={{ borderRadius: 8 }}
                loading={trendsLoading}
              >
                <Chart height={280} data={dvScore} forceFit>
                  <Axis
                    name="week"
                    label={{
                      formatter: val => (scoreVisibleTicks.has(String(val)) ? String(val) : '')
                    }}
                  />
                  <Axis name="value" />
                  <ChartTooltip />
                  <Geom
                    type="line"
                    position="week*value"
                    color="type"
                    size={2}
                    connectNulls
                    shape={['segment', seg => (String(seg) === 'incomplete' ? 'dash' : 'line')]}
                  />
                  {showTrendPoints && (
                    <Geom
                      type="point"
                      position="week*value"
                      color="type"
                      size={['segment', seg => (String(seg) === 'incomplete' ? 0 : 2)]}
                      shape="circle"
                    />
                  )}
                </Chart>
              </Card>
              {scoreDeltaCard && (
                <div
                  style={{
                    marginTop: 12,
                    borderRadius: 8,
                    border: '1px solid #f0f0f0',
                    padding: 14,
                    background: '#fafafa'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                    <div style={{ fontSize: 12, color: '#595959' }}>Average review score</div>
                    <Tooltip title={TOOLTIP_COPY.trends.wowYoyDelta}>
                      <Icon type="info-circle" style={{ color: '#8c8c8c', fontSize: 14 }} />
                    </Tooltip>
                  </div>
                  <div style={{ marginTop: 6, fontSize: 22, fontWeight: 600, color: '#262626' }}>
                    {formatToTwoDecimals(scoreDeltaCard.latest.score)}
                  </div>
                  <div
                    style={{
                      marginTop: 6,
                      fontSize: 12,
                      color: scoreDeltaCard.wowDelta != null ? (scoreDeltaCard.wowDelta >= 0 ? '#52c41a' : '#ff4d4f') : '#595959'
                    }}
                  >
                    {scoreDeltaCard.wowDelta != null ? (
                      <Icon type={scoreDeltaCard.wowDelta >= 0 ? 'arrow-up' : 'arrow-down'} style={{ marginRight: 6 }} />
                    ) : (
                      <span style={{ width: 14, marginRight: 6, display: 'inline-block' }} />
                    )}
                    {formatSignedDelta(scoreDeltaCard.wowDelta, 2)} WoW
                  </div>
                  <div
                    style={{
                      marginTop: 4,
                      fontSize: 12,
                      color: scoreDeltaCard.yoyDelta != null ? (scoreDeltaCard.yoyDelta >= 0 ? '#52c41a' : '#ff4d4f') : '#595959'
                    }}
                  >
                    {scoreDeltaCard.yoyDelta != null ? (
                      <Icon type={scoreDeltaCard.yoyDelta >= 0 ? 'arrow-up' : 'arrow-down'} style={{ marginRight: 6 }} />
                    ) : (
                      <span style={{ width: 14, marginRight: 6, display: 'inline-block' }} />
                    )}
                    {formatSignedDelta(scoreDeltaCard.yoyDelta, 2)} YoY
                  </div>
                </div>
              )}
            </Col>
            <Col xs={24} lg={12}>
              <Card
                title={
                  <span>
                    5-Star Review %
                    <Tooltip title={TOOLTIP_COPY.trends.fiveStarReviewPct}>
                      <Icon type="info-circle" style={{ marginLeft: 8, color: '#8c8c8c' }} />
                    </Tooltip>
                  </span>
                }
                size="small"
                style={{ borderRadius: 8 }}
                loading={trendsLoading}
              >
                <Chart height={280} data={dvFiveStar} forceFit>
                  <Axis
                    name="week"
                    label={{
                      formatter: val => (fiveStarVisibleTicks.has(String(val)) ? String(val) : '')
                    }}
                  />
                  <Axis name="value" label={{ formatter: v => `${v}%` }} />
                  <ChartTooltip formatter={v => `${v}%`} />
                  <Geom
                    type="line"
                    position="week*value"
                    color="type"
                    size={2}
                    connectNulls
                    shape={['segment', seg => (String(seg) === 'incomplete' ? 'dash' : 'line')]}
                  />
                  {showTrendPoints && (
                    <Geom
                      type="point"
                      position="week*value"
                      color="type"
                      size={['segment', seg => (String(seg) === 'incomplete' ? 0 : 2)]}
                      shape="circle"
                    />
                  )}
                </Chart>
              </Card>
              {fiveStarDeltaCard && (
                <div
                  style={{
                    marginTop: 12,
                    borderRadius: 8,
                    border: '1px solid #f0f0f0',
                    padding: 14,
                    background: '#fafafa'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                    <div style={{ fontSize: 12, color: '#595959' }}>5-star review rate</div>
                    <Tooltip title={TOOLTIP_COPY.trends.wowYoyDelta}>
                      <Icon type="info-circle" style={{ color: '#8c8c8c', fontSize: 14 }} />
                    </Tooltip>
                  </div>
                  <div style={{ marginTop: 6, fontSize: 22, fontWeight: 600, color: '#262626' }}>
                    {formatToTwoDecimals(fiveStarDeltaCard.latest.rate)}
                  </div>
                  <div
                    style={{
                      marginTop: 6,
                      fontSize: 12,
                      color: fiveStarDeltaCard.wowDelta != null ? (fiveStarDeltaCard.wowDelta >= 0 ? '#52c41a' : '#ff4d4f') : '#595959'
                    }}
                  >
                    {fiveStarDeltaCard.wowDelta != null ? (
                      <Icon type={fiveStarDeltaCard.wowDelta >= 0 ? 'arrow-up' : 'arrow-down'} style={{ marginRight: 6 }} />
                    ) : (
                      <span style={{ width: 14, marginRight: 6, display: 'inline-block' }} />
                    )}
                    {formatSignedDelta(fiveStarDeltaCard.wowDelta, 2)} WoW
                  </div>
                  <div
                    style={{
                      marginTop: 4,
                      fontSize: 12,
                      color: fiveStarDeltaCard.yoyDelta != null ? (fiveStarDeltaCard.yoyDelta >= 0 ? '#52c41a' : '#ff4d4f') : '#595959'
                    }}
                  >
                    {fiveStarDeltaCard.yoyDelta != null ? (
                      <Icon type={fiveStarDeltaCard.yoyDelta >= 0 ? 'arrow-up' : 'arrow-down'} style={{ marginRight: 6 }} />
                    ) : (
                      <span style={{ width: 14, marginRight: 6, display: 'inline-block' }} />
                    )}
                    {formatSignedDelta(fiveStarDeltaCard.yoyDelta, 2)} YoY
                  </div>
                </div>
              )}
            </Col>
          </Row>

          {/* Component Evolution – same filters as Trends, line chart with 6 categories and colour legend at bottom */}
          <SectionTitle style={{ marginBottom: 16 }}>
            Review Category Evolution
            <Tooltip title={TOOLTIP_COPY.componentEvolution.chart}>
              <Icon type="info-circle" style={{ marginLeft: 8, color: '#8c8c8c', fontSize: 14 }} />
            </Tooltip>
          </SectionTitle>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 16, marginBottom: 20 }}>
            <span style={{ fontSize: 13, color: '#595959' }}>Granularity:</span>
            <Radio.Group
              value={granularity}
              onChange={e => setGranularity(e.target.value)}
              size="small"
              buttonStyle="solid"
              style={{ marginRight: 24 }}
            >
              <Radio.Button value="weekly">Weekly</Radio.Button>
              <Radio.Button value="monthly">Monthly</Radio.Button>
            </Radio.Group>
            <span style={{ fontSize: 13, color: '#595959' }}>Lookback:</span>
            <Select value={lookback} onChange={setLookback} style={{ width: 140 }} size="small">
              <Option value="1">1 month</Option>
              <Option value="3">3 months</Option>
              <Option value="6">6 months</Option>
              <Option value="12">1 year</Option>
              <Option value="24">2 years</Option>
              <Option value="all">All time</Option>
            </Select>
          </div>
          <Card
            title={`Review Category Evolution (${granularity === 'weekly' ? 'Weekly' : 'Monthly'})`}
            size="small"
            style={{ marginBottom: 32, borderRadius: 8 }}
            loading={componentEvolutionLoading}
          >
            <Chart height={320} data={dvComponent} forceFit>
              <Axis
                name="week"
                label={{
                  formatter: val => (componentVisibleTicks.has(String(val)) ? String(val) : '')
                }}
              />
              <Axis name="rate" title={{ text: 'Rate' }} />
              <ChartTooltip />
              <Geom
                type="line"
                position="week*rate"
                color={['category', cat => componentEvolutionColors[cat] || '#999']}
                size={2}
                connectNulls
                shape={['segment', seg => (String(seg) === 'incomplete' ? 'dash' : 'line')]}
              />
              {showComponentPoints && (
                <Geom
                  type="point"
                  position="week*rate"
                  color={['category', cat => componentEvolutionColors[cat] || '#999']}
                  size={['segment', seg => (String(seg) === 'incomplete' ? 0 : 2)]}
                  shape="circle"
                />
              )}
              <Legend position="bottom" visible={false} />
            </Chart>
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'center',
                gap: '16px 24px',
                marginTop: 16,
                paddingTop: 12,
                borderTop: '1px solid #f0f0f0'
              }}
            >
              {componentEvolutionFields.map(cat => (
                <span key={cat} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#262626' }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: componentEvolutionColors[cat], flexShrink: 0 }} />
                  {cat}
                </span>
              ))}
            </div>
          </Card>

          {componentDeltaCards && componentDeltaCards.length > 0 && (
            <div style={{ marginBottom: 32 }}>
              <Row gutter={[16, 16]}>
                {componentDeltaCards.map(card => {
                  const wowHas = card.wowDelta != null;
                  const wowColor = !wowHas ? '#595959' : card.wowDelta >= 0 ? '#52c41a' : '#ff4d4f';
                  const yoyHas = card.yoyDelta != null;
                  const yoyColor = !yoyHas ? '#595959' : card.yoyDelta >= 0 ? '#52c41a' : '#ff4d4f';
                  return (
                    <Col xs={24} sm={12} md={8} lg={4} key={card.cat}>
                      <Card size="small" style={{ borderRadius: 8, border: '1px solid #e8e8e8' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                          <div style={{ fontSize: 12, color: '#666', fontWeight: 600 }}>{card.cat}</div>
                          <span style={{ fontSize: 12, color: componentEvolutionColors[card.cat] || '#8c8c8c' }}>●</span>
                        </div>
                        <div style={{ marginTop: 10, fontSize: 18, fontWeight: 700, color: '#262626' }}>{formatToTwoDecimals(card.latestVal)}</div>
                        <div style={{ marginTop: 10, fontSize: 12, color: wowColor, display: 'flex', alignItems: 'center', gap: 6 }}>
                          {wowHas ? (
                            <Icon type={card.wowDelta >= 0 ? 'arrow-up' : 'arrow-down'} style={{ color: wowColor }} />
                          ) : (
                            <span style={{ width: 14, display: 'inline-block' }} />
                          )}
                          {formatSignedDelta(card.wowDelta, 2)} WoW
                        </div>
                        <div style={{ marginTop: 6, fontSize: 12, color: yoyColor, display: 'flex', alignItems: 'center', gap: 6 }}>
                          {yoyHas ? (
                            <Icon type={card.yoyDelta >= 0 ? 'arrow-up' : 'arrow-down'} style={{ color: yoyColor }} />
                          ) : (
                            <span style={{ width: 14, display: 'inline-block' }} />
                          )}
                          {formatSignedDelta(card.yoyDelta, 2)} YoY
                        </div>
                      </Card>
                    </Col>
                  );
                })}
              </Row>
            </div>
          )}

          <QualityDashboardQualityStatsSection
            airbnbId={selectedAirbnbUserId}
            listingLabelById={listingLabelById}
            filterQuery={qualityStatsFilterQuery}
          />

          <div ref={listingsSectionRef} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <SectionTitle style={{ marginBottom: 0 }}>Listings</SectionTitle>
          </div>
          <Table
            columns={listingsColumns}
            dataSource={listingsRows}
            loading={dashboardLoading}
            onChange={(pagination, filters, sorter) =>
              handleOnAggregationTableChange({ pagination, filters, sorter: sorter || {} }, setListingsQuery)
            }
            pagination={{
              total: listingsPaginationTotal,
              current: listingsQuery.currentPage,
              pageSize: listingsQuery.limit || 10,
              showLessItems: true,
              hideOnSinglePage: false
            }}
          />
        </Tabs.TabPane>

        <Tabs.TabPane tab="Reviews Quality" key="reviews">
          <div style={{ marginBottom: 24 }}>
            <SectionTitle style={{ marginBottom: 16 }}>Reviews</SectionTitle>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: 24, marginBottom: 24 }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 8 }}>
                <span style={{ fontSize: 36, fontWeight: 600, color: '#262626' }}>
                  ★ {reviewOverallRating != null ? Number(reviewOverallRating).toFixed(2) : '—'}
                </span>
                <span style={{ fontSize: 13, color: '#595959' }}>{reviewTotalCount.toLocaleString()} Reviews</span>
                <Button
                  type="default"
                  size="small"
                  style={{ borderColor: '#ff4d4f', color: '#ff4d4f' }}
                  onClick={() => setShowReviewTags(!showReviewTags)}
                >
                  {showReviewTags ? 'Hide review tags' : 'Show review tags'}
                </Button>
              </div>
              <Row gutter={[16, 16]} style={{ flex: 1, minWidth: 0 }}>
                {reviewCategoryCards.map(cat => (
                  <Col xs={24} sm={12} md={8} lg={6} key={cat.key}>
                    <Card size="small" style={{ borderRadius: 8, border: '1px solid #e8e8e8', position: 'relative' }}>
                      <Tooltip title={TOOLTIP_COPY.reviews.categoryScoreDistribution}>
                        <Icon type="info-circle" style={{ position: 'absolute', top: 8, right: 8, color: '#8c8c8c', fontSize: 14 }} />
                      </Tooltip>
                      <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>{cat.label}</div>
                      <div style={{ fontSize: 18, fontWeight: 600, color: '#262626', marginBottom: 8 }}>★ {cat.rating}</div>
                      <div style={{ display: 'flex', height: 8, borderRadius: 4, overflow: 'hidden', background: '#f0f0f0' }}>
                        <div style={{ width: `${cat.greenPct}%`, background: '#52c41a' }} />
                        <div style={{ width: `${cat.redPct}%`, background: '#ff4d4f' }} />
                      </div>
                      <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 4 }}>
                        <span style={{ color: '#52c41a' }}>{cat.greenPct}%</span>
                        <span style={{ margin: '0 4px' }}>/</span>
                        <span style={{ color: '#ff4d4f' }}>{cat.redPct}%</span>
                      </div>
                    </Card>
                  </Col>
                ))}
              </Row>
            </div>

            {showReviewTags && (
              <div style={{ marginBottom: 24, padding: 16, background: '#fafafa', borderRadius: 8, border: '1px solid #e8e8e8' }}>
                <SectionTitle style={{ marginBottom: 16 }}>Review Tags</SectionTitle>
                <Row gutter={[16, 16]}>
                  {reviewCategoryCards.map(cat => {
                    const data = reviewTagsByCategoryMapped[cat.key];
                    if (!data) return null;
                    return (
                      <Col xs={24} sm={12} md={8} key={cat.key}>
                        <Card
                          size="small"
                          style={{ borderRadius: 8, border: '1px solid #e8e8e8', height: 320 }}
                          bodyStyle={{ height: 288, display: 'flex', flexDirection: 'column', padding: 16, overflow: 'hidden' }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12, flexShrink: 0 }}>
                            <Icon type="message" style={{ color: '#1890ff' }} />
                            <span style={{ fontWeight: 600 }}>{cat.label}</span>
                            <span style={{ fontSize: 12, color: '#8c8c8c' }}>({data.total})</span>
                          </div>
                          <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
                            <div style={{ marginBottom: 8 }}>
                              <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 4 }}>Positive</div>
                              {(data.positive || []).map((p, i) => (
                                <div key={i} style={{ fontSize: 13, color: '#52c41a', marginBottom: 2 }}>
                                  {p.tag} <span style={{ color: '#8c8c8c' }}>({p.count})</span>
                                </div>
                              ))}
                            </div>
                            <div>
                              <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 4 }}>Negative</div>
                              {(data.negative || []).map((n, i) => (
                                <div key={i} style={{ fontSize: 13, color: '#ff4d4f', marginBottom: 2 }}>
                                  {n.tag} <span style={{ color: '#8c8c8c' }}>({n.count})</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </Card>
                      </Col>
                    );
                  })}
                </Row>
              </div>
            )}

            <Row gutter={24}>
              <Col xs={24} md={12}>
                <div style={{ marginBottom: 16 }}>
                  <SectionTitle style={{ marginBottom: 12 }}>
                    Violations
                    <Tooltip title={TOOLTIP_COPY.reviews.violations}>
                      <Icon type="info-circle" style={{ marginLeft: 8, color: '#8c8c8c', fontSize: 14 }} />
                    </Tooltip>
                  </SectionTitle>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                    <span style={{ fontSize: 32, fontWeight: 600, color: '#ff4d4f' }}>{reviewViolationsTotal}</span>
                    <Icon type="safety" style={{ fontSize: 28, color: '#ff4d4f' }} />
                  </div>
                  {reviewViolationsMapped.map((v, idx) => (
                    <div key={idx} style={{ marginBottom: 12 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                        <span style={{ fontSize: 13 }}>
                          <Icon type={v.icon} style={{ marginRight: 6 }} />
                          {v.label}
                        </span>
                        <span style={{ fontWeight: 600 }}>{v.count}</span>
                      </div>
                      <Progress
                        percent={maxViolation ? Math.round((v.count / maxViolation) * 100) : 0}
                        showInfo={false}
                        strokeColor="#ff4d4f"
                        size="small"
                      />
                    </div>
                  ))}
                </div>
              </Col>
              <Col xs={24} md={12}>
                <div>
                  <SectionTitle style={{ marginBottom: 8 }}>
                    Accommodation status
                    <Tooltip title={TOOLTIP_COPY.reviews.accommodationStatus}>
                      <Icon type="info-circle" style={{ marginLeft: 8, color: '#8c8c8c', fontSize: 14 }} />
                    </Tooltip>
                  </SectionTitle>
                  <p style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 12 }}>
                    Review the quality status of your account and your listings by hovering over each category.
                  </p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {accommodationStatusesMapped.map((s, idx) => (
                      <Tag key={idx} color={s.color} style={{ marginBottom: 4 }}>
                        {s.label} ({s.count})
                      </Tag>
                    ))}
                  </div>
                  <div style={{ marginTop: 16, width: '100%', maxWidth: 420, height: 340 }}>
                    {accommodationPieData.length > 0 ? (
                      <Chart data={accommodationDv} scale={accommodationCols} forceFit height={340}>
                        <Coord type="theta" radius={0.75} innerRadius={0.6} />
                        <Axis name="percent" />
                        <Geom
                          type="intervalStack"
                          position="percent"
                          color={[
                            'type',
                            type => {
                              const match = accommodationPieData.find(d => d.type === type);
                              return (match && match.color) || '#999';
                            }
                          ]}
                          style={{ lineWidth: 1, stroke: '#fff' }}
                          tooltip={[
                            'type*percent',
                            (type, percent) => {
                              const match = accommodationPieData.find(d => d.type === type);
                              return {
                                name: type,
                                value: match && match.count != null ? match.count : 0
                              };
                            }
                          ]}
                        >
                          <Label content="percent" formatter={(val, item) => item.point.type} />
                        </Geom>
                        <ChartTooltip showTitle={false} />
                      </Chart>
                    ) : (
                      <div
                        style={{
                          width: 120,
                          height: 120,
                          borderRadius: '50%',
                          border: '3px solid #e8e8e8',
                          background: '#fafafa',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 12,
                          color: '#8c8c8c'
                        }}
                      >
                        {TOOLTIP_COPY.emptyState.noData}
                      </div>
                    )}
                  </div>
                </div>
              </Col>
            </Row>
          </div>
        </Tabs.TabPane>
        <Tabs.TabPane tab="Checkout Tasks" key="checkoutTasks">
          <SectionTitle style={{ marginBottom: 12 }}>Checkout Tasks (Dummy Data)</SectionTitle>
          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            <Col xs={24} sm={12} md={6}>
              <KPICard>
                <div style={{ fontSize: 12, color: '#666' }}>Open tasks</div>
                <div style={{ marginTop: 6, fontSize: 24, fontWeight: 700 }}>{checkoutKpis.openTasks}</div>
              </KPICard>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <KPICard>
                <div style={{ fontSize: 12, color: '#666' }}>Overdue</div>
                <div style={{ marginTop: 6, fontSize: 24, fontWeight: 700, color: '#ff4d4f' }}>{checkoutKpis.overdue}</div>
              </KPICard>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <KPICard>
                <div style={{ fontSize: 12, color: '#666' }}>Completed today</div>
                <div style={{ marginTop: 6, fontSize: 24, fontWeight: 700, color: '#52c41a' }}>{checkoutKpis.completedToday}</div>
              </KPICard>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <KPICard>
                <div style={{ fontSize: 12, color: '#666' }}>Avg completion time</div>
                <div style={{ marginTop: 6, fontSize: 24, fontWeight: 700 }}>{checkoutKpis.avgCompletion}m</div>
              </KPICard>
            </Col>
          </Row>

          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
            <Select value={taskStatusFilter} style={{ width: 180 }} onChange={setTaskStatusFilter}>
              <Option value="ALL">All status</Option>
              <Option value="Pending">Pending</Option>
              <Option value="In Progress">In Progress</Option>
              <Option value="Done">Done</Option>
              <Option value="Overdue">Overdue</Option>
            </Select>
            <Select value={taskPriorityFilter} style={{ width: 180 }} onChange={setTaskPriorityFilter}>
              <Option value="ALL">All priority</Option>
              <Option value="Critical">Critical</Option>
              <Option value="High">High</Option>
              <Option value="Medium">Medium</Option>
              <Option value="Low">Low</Option>
            </Select>
          </div>
          <Table
            rowKey="id"
            size="small"
            columns={checkoutTaskColumns}
            dataSource={filteredCheckoutTasks}
            pagination={{ pageSize: 6, showLessItems: true }}
            scroll={{ x: 1000 }}
          />
        </Tabs.TabPane>
        <Tabs.TabPane tab="Host Notifications" key="hostNotifications">
          <SectionTitle style={{ marginBottom: 12 }}>Host Notifications (Dummy Data)</SectionTitle>
          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            <Col xs={24} sm={12} md={6}>
              <KPICard>
                <div style={{ fontSize: 12, color: '#666' }}>Unread notifications</div>
                <div style={{ marginTop: 6, fontSize: 24, fontWeight: 700 }}>{notificationsKpis.unread}</div>
              </KPICard>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <KPICard>
                <div style={{ fontSize: 12, color: '#666' }}>Critical</div>
                <div style={{ marginTop: 6, fontSize: 24, fontWeight: 700, color: '#ff4d4f' }}>{notificationsKpis.critical}</div>
              </KPICard>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <KPICard>
                <div style={{ fontSize: 12, color: '#666' }}>Warning</div>
                <div style={{ marginTop: 6, fontSize: 24, fontWeight: 700, color: '#fa8c16' }}>{notificationsKpis.warning}</div>
              </KPICard>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <KPICard>
                <div style={{ fontSize: 12, color: '#666' }}>Info</div>
                <div style={{ marginTop: 6, fontSize: 24, fontWeight: 700, color: '#1890ff' }}>{notificationsKpis.info}</div>
              </KPICard>
            </Col>
          </Row>
          <Card size="small" style={{ borderRadius: 8 }}>
            <Timeline>
              {hostNotifications.map(item => (
                <Timeline.Item key={item.id} color={SEVERITY_COLOR[item.severity] || 'blue'} dot={<Badge dot={!item.read}>{null}</Badge>}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                        <Tag color={SEVERITY_COLOR[item.severity]}>{item.severity}</Tag>
                        <Tag>{item.category}</Tag>
                        <span style={{ fontWeight: 600 }}>{item.title}</span>
                      </div>
                      <div style={{ fontSize: 13, color: '#595959' }}>{item.message}</div>
                      <div style={{ marginTop: 4, fontSize: 12, color: '#8c8c8c' }}>
                        Listing: {item.listing} · {moment(item.createdAt, 'YYYY-MM-DD HH:mm').format('YYYY-MM-DD HH:mm')}
                      </div>
                    </div>
                    <div>
                      <Button
                        size="small"
                        disabled={item.read}
                        onClick={() => {
                          setHostNotifications(prev => prev.map(n => (n.id === item.id ? { ...n, read: true } : n)));
                        }}
                      >
                        {item.read ? 'Read' : 'Mark as read'}
                      </Button>
                    </div>
                  </div>
                </Timeline.Item>
              ))}
            </Timeline>
          </Card>
        </Tabs.TabPane>
        <Tabs.TabPane tab="Ground Rules Notifications" key="groundRulesNotifications">
          <SectionTitle style={{ marginBottom: 12 }}>Ground Rules Notifications (Dummy Data)</SectionTitle>
          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            <Col xs={24} sm={12} md={6}>
              <KPICard>
                <div style={{ fontSize: 12, color: '#666' }}>Triggered</div>
                <div style={{ marginTop: 6, fontSize: 24, fontWeight: 700 }}>{groundRulesKpis.triggered}</div>
              </KPICard>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <KPICard>
                <div style={{ fontSize: 12, color: '#666' }}>Acknowledged</div>
                <div style={{ marginTop: 6, fontSize: 24, fontWeight: 700, color: '#fa8c16' }}>{groundRulesKpis.acknowledged}</div>
              </KPICard>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <KPICard>
                <div style={{ fontSize: 12, color: '#666' }}>Escalated</div>
                <div style={{ marginTop: 6, fontSize: 24, fontWeight: 700, color: '#eb2f96' }}>{groundRulesKpis.escalated}</div>
              </KPICard>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <KPICard>
                <div style={{ fontSize: 12, color: '#666' }}>Resolved</div>
                <div style={{ marginTop: 6, fontSize: 24, fontWeight: 700, color: '#52c41a' }}>{groundRulesKpis.resolved}</div>
              </KPICard>
            </Col>
          </Row>
          <Table
            rowKey="id"
            size="small"
            columns={groundRulesColumns}
            dataSource={GROUND_RULE_NOTIFICATIONS}
            expandable={{
              expandedRowRender: row => <div style={{ margin: 0, color: '#595959' }}>{row.details}</div>
            }}
            pagination={{ pageSize: 6, showLessItems: true }}
            scroll={{ x: 900 }}
          />
        </Tabs.TabPane>
      </Tabs>

      <Modal
        title="Filters"
        visible={filtersVisible}
        onCancel={() => setFiltersVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setFiltersVisible(false)}>
            Cancel
          </Button>,
          <Button key="apply" type="primary" onClick={handleApplyFilters}>
            Apply
          </Button>
        ]}
      >
        <div style={{ marginBottom: 12 }}>
          <div style={{ marginBottom: 4, fontSize: 12, color: '#666' }}>Date Range</div>
          {getFieldDecorator('dateRange')(<RangePicker style={{ width: '100%' }} />)}
        </div>
        <div style={{ marginBottom: 12 }}>
          <div style={{ marginBottom: 4, fontSize: 12, color: '#666' }}>Listing ID</div>
          {getFieldDecorator('listing')(<Input placeholder="e.g. 123456789" />)}
        </div>
        <div style={{ marginBottom: 12 }}>
          <div style={{ marginBottom: 4, fontSize: 12, color: '#666' }}>Country</div>
          {getFieldDecorator('country')(
            <Select
              showSearch
              style={{ width: '100%' }}
              placeholder="Country"
              loading={countriesLoading}
              filterOption={(input, option) => {
                if (!input) return true;
                const regex = new RegExp(input, 'i');
                return regex.test(option.props.children);
              }}
              onChange={value => {
                setModalCountryCode(value || null);
                setFieldsValue({ state: undefined, city: undefined });
              }}
            >
              {countries.map(item => (
                <Option key={item.code} value={item.code}>
                  {item.code} - {item.label}
                </Option>
              ))}
            </Select>
          )}
        </div>
        {(() => {
          const hasCountry = Boolean(getFieldValue('country'));
          const isMys = String(getFieldValue('country')) === 'MY';
          return (
            <>
              <div style={{ marginBottom: 12 }}>
                <div style={{ marginBottom: 4, fontSize: 12, color: '#666' }}>State</div>
                {isMys
                  ? getFieldDecorator('state')(
                      <Select
                        showSearch
                        style={{ width: '100%' }}
                        placeholder="State"
                        loading={statesLoading}
                        disabled={!hasCountry}
                        filterOption={(input, option) => {
                          if (!input) return true;
                          const regex = new RegExp(input, 'i');
                          return regex.test(option.props.children);
                        }}
                      >
                        {statesList.map(item => (
                          <Option key={item.code} value={item.label}>
                            {item.label}
                          </Option>
                        ))}
                      </Select>
                    )
                  : getFieldDecorator('state')(<Input placeholder="Enter State" disabled={!hasCountry} />)}
              </div>
              <div style={{ marginBottom: 12 }}>
                <div style={{ marginBottom: 4, fontSize: 12, color: '#666' }}>City</div>
                {getFieldDecorator('city')(<Input placeholder="e.g. Genting Highlands" />)}
              </div>
            </>
          );
        })()}
        <div style={{ marginBottom: 12 }}>
          <div style={{ marginBottom: 4, fontSize: 12, color: '#666' }}>Latest Quality Status</div>
          {getFieldDecorator('qualityStatus', { initialValue: 'ALL' })(
            <Select style={{ width: '100%' }}>
              <Option value="ALL">All</Option>
              <Option value="good">Good</Option>
              <Option value="warning">Warn</Option>
              <Option value="at_risk">At risk</Option>
              <Option value="suspended">Suspended</Option>
            </Select>
          )}
        </div>
      </Modal>

      <Modal
        title="Airbnb issues, cancellations and violations"
        visible={issuesModalVisible}
        onCancel={() => setIssuesModalVisible(false)}
        footer={
          <Button type="primary" onClick={() => setIssuesModalVisible(false)}>
            Ok
          </Button>
        }
        width={560}
      >
        {issuesModalLoading ? (
          <div style={{ padding: '20px 0', textAlign: 'center', color: '#8c8c8c' }}>Loading issues...</div>
        ) : (
          selectedListingForModal && (
            <>
              {(selectedListingForModal.modalReviewIssues || []).length > 0 && (
                <div style={{ marginBottom: 24 }}>
                  <div style={{ fontWeight: 600, marginBottom: 12, color: '#262626' }}>Airbnb review issues</div>
                  {(selectedListingForModal.modalReviewIssues || []).map((item, idx) => (
                    <div key={idx} style={{ marginBottom: 16 }}>
                      <div style={{ marginBottom: 4 }}>
                        <span style={{ marginRight: 8 }}>Reservation ID: {item.reservationId}</span>
                        <Button
                          type="link"
                          size="small"
                          style={{ padding: 0, fontSize: 13 }}
                          disabled={!item.reservationObjectId}
                          onClick={() => {
                            const url = buildReservationReviewUrl(item.reservationObjectId);
                            if (url) window.open(url, '_blank');
                          }}
                        >
                          Review information
                        </Button>
                      </div>
                      <ul style={{ margin: 0, paddingLeft: 20, fontSize: 13, color: '#595959' }}>
                        {(item.issues || []).map((issue, i) => (
                          <li key={i}>{issue}</li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              )}
              {(selectedListingForModal.modalViolations || []).length > 0 && (
                <div>
                  <div style={{ fontWeight: 600, marginBottom: 12, color: '#262626' }}>Airbnb violations</div>
                  {(selectedListingForModal.modalViolations || []).map((item, idx) => (
                    <div key={idx} style={{ marginBottom: 16 }}>
                      <div style={{ marginBottom: 4 }}>
                        <span style={{ marginRight: 8 }}>Reservation ID: {item.reservationId}</span>
                        <Button
                          type="link"
                          size="small"
                          style={{ padding: 0, fontSize: 13 }}
                          disabled={!item.reservationObjectId}
                          onClick={() => {
                            const url = buildReservationReviewUrl(item.reservationObjectId);
                            if (url) window.open(url, '_blank');
                          }}
                        >
                          Review information
                        </Button>
                      </div>
                      <ul style={{ margin: 0, paddingLeft: 20, fontSize: 13, color: '#595959' }}>
                        {(item.violations || []).map((v, i) => (
                          <li key={i}>{v}</li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              )}
              {(selectedListingForModal.modalReviewIssues || []).length === 0 && (selectedListingForModal.modalViolations || []).length === 0 && (
                <div style={{ color: '#8c8c8c', fontSize: 13 }}>No issues or violations for this listing.</div>
              )}
            </>
          )
        )}
      </Modal>
    </StyledCard>
  );
};

export default Form.create()(QualityDashboardDemo8);
